import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Row, Col, Button, Select, DatePicker, Card, Statistic, Checkbox, message, Tag, Empty, Tooltip, Switch, InputNumber, Modal, Input, Dropdown, Skeleton } from 'antd';
import {
  DownloadOutlined,
  PlayCircleOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  DownOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faUsers,
  faSyringe,
  faChartPie,
  faChartBar,
  faHeartPulse,
  faTriangleExclamation,
  faFlask,
  faClock,
  faUserCheck,
  faFilter,
  faChevronDown,
  faChevronRight,
  faWeightScale,
  faBolt,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { usePageHeader } from '../../../layouts/AdminLayout';
import { PageContainer } from '../styled';
import { COLORS } from '../../../constants/colors';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const METRIC_CATEGORIES = [
  {
    category: 'Events',
    group: 'Event & Operations',
    icon: <FontAwesomeIcon icon={faCalendarAlt} />,
    modalContent: {
      title: 'Events',
      description: 'Tracks unique event types and the average number of screenings conducted per event.',
      metrics: [
        { name: 'Total Events', detail: 'Counts the number of distinct event types that have at least one booking. Each unique event type is counted once regardless of how many bookings it has.' },
        { name: 'Avg Screenings per Event', detail: 'Total completed screenings divided by the number of unique event types. Helps gauge average throughput per event.' }
      ]
    },
    metrics: [
      { key: 'totalEvents', label: 'Total Events', description: 'Number of unique event types with bookings' },
      { key: 'avgScreeningsPerEvent', label: 'Avg Screenings per Event', description: 'Average number of screenings across events' }
    ]
  },
  {
    category: 'Screenings',
    group: 'Event & Operations',
    icon: <FontAwesomeIcon icon={faSyringe} />,
    modalContent: {
      title: 'Screenings',
      description: 'Overall screening visit counts broken down by completion status.',
      metrics: [
        { name: 'Total Screenings', detail: 'All visits/screenings recorded in the system for the selected date range, regardless of status.' },
        { name: 'Completed Screenings', detail: 'Visits with a completed or screened status, indicating the screening process was finished.' },
        { name: 'No Shows', detail: 'Members who were registered but did not attend their scheduled screening.' }
      ]
    },
    metrics: [
      { key: 'totalScreenings', label: 'Total Screenings', description: 'Total visits/screenings recorded' },
      { key: 'completedScreenings', label: 'Completed Screenings', description: 'Screenings marked as completed' },
      { key: 'noShows', label: 'No Shows', description: 'Members who did not show up' }
    ]
  },
  {
    category: 'Gender',
    group: 'Demographics',
    icon: <FontAwesomeIcon icon={faUsers} />,
    modalContent: {
      title: 'Gender',
      description: 'Gender distribution of screened members based on birth gender recorded in their profile.',
      metrics: [
        { name: 'Males / Females / Other', detail: 'Raw counts of members by gender category.' },
        { name: 'Male % / Female % / Other %', detail: 'Percentage of total screened members falling into each gender category.' }
      ]
    },
    metrics: [
      { key: 'males', label: 'Males', description: 'Total male members' },
      { key: 'females', label: 'Females', description: 'Total female members' },
      { key: 'other', label: 'Other', description: 'Other/unknown gender members' },
      { key: 'malePercent', label: 'Male %', description: 'Percentage of male members', suffix: '%' },
      { key: 'femalePercent', label: 'Female %', description: 'Percentage of female members', suffix: '%' },
      { key: 'otherPercent', label: 'Other %', description: 'Percentage of other/unknown', suffix: '%' }
    ]
  },
  {
    category: 'Demographics',
    group: 'Demographics',
    icon: <FontAwesomeIcon icon={faChartPie} />,
    modalContent: {
      title: 'Demographics',
      description: 'Population breakdowns to understand the communities being served.',
      metrics: [
        { name: 'Age Breakdown', detail: 'Distribution of members across age groups. Groups are customizable via the "Age Groups" control in the builder (default: 18-30, 31-45, 46-60, 61-75, 75+). Calculated from date of birth.' },
        { name: 'Ethnicity Breakdown', detail: 'Distribution by self-reported race/ethnicity categories.' },
        { name: 'Insurance Breakdown', detail: 'Insured vs. Uninsured vs. Unknown, derived from the intake form\u2019s insurance coverage question.' },
        { name: 'Household Income Breakdown', detail: 'Distribution across the 8 canonical income buckets (plus Unknown for unanswered). Some values are statistically modeled rather than self-reported - see the imputed count.' },
        { name: 'Stress Breakdown', detail: 'Low / Moderate / High buckets from the intake form\u2019s 1\u20135 stress rating (Low 1\u20132, Moderate 3, High 4\u20135).' },
        { name: 'County Breakdown', detail: 'Distribution by county, derived from each member\u2019s ZIP via the HUD ZIP-County crosswalk.' },
        { name: 'ZIP Code Breakdown', detail: 'How many members were seen in each ZIP code, with the county each ZIP belongs to.' }
      ]
    },
    metrics: [
      { key: 'ageBreakdown', label: 'Age Breakdown', description: 'Distribution by age groups', isBreakdown: true },
      { key: 'ethnicityBreakdown', label: 'Ethnicity Breakdown', description: 'Distribution by race/ethnicity', isBreakdown: true },
      { key: 'insuranceBreakdown', label: 'Insurance Breakdown', description: 'Insured vs. Uninsured vs. Unknown', isBreakdown: true },
      { key: 'uninsuredPercent', label: 'Uninsured %', description: 'Percent of cohort with no insurance', suffix: '%' },
      { key: 'incomeBreakdown', label: 'Household Income Breakdown', description: 'Distribution across the 8 income buckets', isBreakdown: true },
      { key: 'lowIncomePercent', label: 'Low Income %', description: 'Percent earning under $50,000', suffix: '%' },
      { key: 'stressBreakdown', label: 'Stress Breakdown', description: 'Low / Moderate / High stress distribution', isBreakdown: true },
      { key: 'avgStress', label: 'Avg Stress (1-5)', description: 'Average self-reported stress rating' },
      { key: 'highStressPercent', label: 'High Stress %', description: 'Percent rating stress High (4-5)', suffix: '%' },
      { key: 'countyBreakdown', label: 'County Breakdown', description: 'Distribution by county (from ZIP)', isBreakdown: true },
      { key: 'zipBreakdown', label: 'ZIP Code Breakdown', description: 'Members seen per ZIP code', isBreakdown: true }
    ]
  },
  {
    category: 'Bookings',
    group: 'Event & Operations',
    icon: <FontAwesomeIcon icon={faChartBar} />,
    modalContent: {
      title: 'Bookings',
      description: 'Registration and booking funnel metrics.',
      metrics: [
        { name: 'Total Bookings', detail: 'All booking registrations created in the selected date range.' },
        { name: 'Cancelled Bookings', detail: 'Bookings that were cancelled before the event.' },
        { name: 'Booking Conversion %', detail: 'Percentage of bookings that were NOT cancelled: (Total - Cancelled) / Total × 100.' }
      ]
    },
    metrics: [
      { key: 'totalBookings', label: 'Total Bookings', description: 'Total booking registrations' },
      { key: 'cancelledBookings', label: 'Cancelled Bookings', description: 'Bookings that were cancelled' },
      { key: 'bookingConversion', label: 'Booking Conversion %', description: 'Percentage of bookings not cancelled', suffix: '%' }
    ]
  },
  {
    category: 'Health Outcomes',
    group: 'Clinical Outcomes',
    icon: <FontAwesomeIcon icon={faHeartPulse} />,
    modalContent: {
      title: 'Health Outcomes',
      description: 'Aggregate clinical metrics from screening data. All averages are calculated from members who have valid readings for that specific test.',
      metrics: [
        { name: 'Blood Pressure', detail: 'Avg Systolic/Diastolic BP in mmHg. High BP defined as systolic ≥ 130 mmHg (AHA guidelines).' },
        { name: 'Cholesterol Panel', detail: 'Avg Total Cholesterol, HDL, LDL, and Triglycerides in mg/dL. High cholesterol defined as total ≥ 240 mg/dL.' },
        { name: 'Lp(a)', detail: 'Average Lipoprotein(a) and count/percent with elevated levels (≥ 125 nmol/L). Lp(a) is a genetic cardiovascular risk factor.' },
        { name: 'Glucose & A1C', detail: 'Average blood glucose (mg/dL) and A1C (%). A1C reflects 2-3 month blood sugar average. Normal A1C < 5.7%.' },
        { name: 'BMI', detail: 'Average Body Mass Index calculated from height and weight. Normal 18.5-24.9, Overweight 25-29.9, Obese ≥ 30.' }
      ]
    },
    metrics: [
      { key: 'avgSystolic', label: 'Avg Systolic BP', description: 'Average systolic blood pressure (mmHg)', suffix: ' mmHg' },
      { key: 'avgDiastolic', label: 'Avg Diastolic BP', description: 'Average diastolic blood pressure (mmHg)', suffix: ' mmHg' },
      { key: 'highBPCount', label: 'High BP Count (systolic ≥130)', description: 'Members with systolic ≥ 130 mmHg (Stage 1 or higher, systolic only). For full ACC/AHA categories use Blood Pressure Analysis.' },
      { key: 'highBPPercent', label: 'High BP % (systolic ≥130)', description: 'Percent of BP-tested members with systolic ≥ 130 mmHg', suffix: '%' },
      { key: 'avgCholesterol', label: 'Avg Total Cholesterol', description: 'Average total cholesterol (mg/dL)', suffix: ' mg/dL' },
      { key: 'highCholesterolCount', label: 'High Cholesterol Count', description: 'Members with cholesterol ≥ 240 mg/dL' },
      { key: 'avgHDL', label: 'Avg HDL', description: 'Average HDL cholesterol (mg/dL)', suffix: ' mg/dL' },
      { key: 'avgLDL', label: 'Avg LDL', description: 'Average LDL cholesterol (mg/dL)', suffix: ' mg/dL' },
      { key: 'avgTriglycerides', label: 'Avg Triglycerides', description: 'Average triglycerides (mg/dL)', suffix: ' mg/dL' },
      { key: 'avgLPA', label: 'Avg Lp(a)', description: 'Average Lipoprotein(a) (nmol/L)', suffix: ' nmol/L' },
      { key: 'elevatedLPACount', label: 'Elevated Lp(a) Count', description: 'Members with Lp(a) ≥ 125 nmol/L' },
      { key: 'elevatedLPAPercent', label: 'Elevated Lp(a) %', description: 'Percentage with elevated Lp(a)', suffix: '%' },
      { key: 'avgGlucose', label: 'Avg Glucose', description: 'Average blood glucose (mg/dL)', suffix: ' mg/dL' },
      { key: 'avgA1C', label: 'Avg A1C', description: 'Average A1C percentage', suffix: '%' },
      { key: 'avgBMI', label: 'Avg BMI', description: 'Average Body Mass Index' }
    ]
  },
  {
    category: 'Risk',
    group: 'Clinical Outcomes',
    icon: <FontAwesomeIcon icon={faTriangleExclamation} />,
    modalContent: {
      title: 'Risk Assessment',
      description: 'Cardiovascular risk scoring based on combined screening data including blood pressure, cholesterol, glucose, BMI, and other factors.',
      metrics: [
        { name: 'High Risk Members', detail: 'Members whose composite risk score places them in the high risk category.' },
        { name: 'Critical Risk Members', detail: 'Members with critically elevated risk scores requiring immediate follow-up.' },
        { name: 'Avg Risk Score', detail: 'Average normalized risk score across all assessed members.' },
        { name: 'Risk Level Breakdown', detail: 'Distribution across all risk levels: Low, Moderate, High, and Critical.' }
      ]
    },
    metrics: [
      { key: 'highRiskMembers', label: 'High Risk Members', description: 'Members with high risk level' },
      { key: 'criticalRiskMembers', label: 'Critical Risk Members', description: 'Members with critical risk level' },
      { key: 'avgRiskScore', label: 'Avg Risk Score', description: 'Average normalized risk score' },
      { key: 'riskBreakdown', label: 'Risk Level Breakdown', description: 'Distribution by risk level', isBreakdown: true }
    ]
  },
  {
    category: 'Blood Pressure Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faHeartPulse} />,
    modalContent: {
      title: 'Blood Pressure Analysis',
      description: 'ACC/AHA 2017 blood-pressure categories using each person\'s most recent valid reading (systolic and diastolic from the same reading). Categories are separate — each person is in exactly one; the "or higher" counts are overlapping thresholds.',
      guidelines: 'Normal: <120/<80 | Elevated: 120–129/<80 | Stage 1: 130–139 or 80–89 | Stage 2: ≥140 or ≥90 | Severe: >180 or >120 (mmHg)',
      metrics: [
        { name: 'Total with BP Results', detail: 'Members with a valid systolic reading on file (the denominator).' },
        { name: 'Threshold Counts', detail: 'Overlapping: Stage 1 or higher (≥130/80), Stage 2 or higher (≥140/90), Severe (>180/120).' },
        { name: 'Stratification', detail: 'Separate, non-overlapping distribution across the 5 categories.' },
        { name: 'Breakdowns', detail: 'Categories within each age group, sex, race/ethnicity, location and event.' }
      ]
    },
    metrics: [
      { key: 'bpTested', label: 'Total with BP Results', description: 'Members with a valid systolic BP reading' },
      { key: 'bpStage1Plus', label: 'Stage 1 or Higher (≥130/80)', description: 'Overlapping threshold: Stage 1, Stage 2 and Severe combined' },
      { key: 'bpStage2Plus', label: 'Stage 2 or Higher (≥140/90)', description: 'Overlapping threshold: Stage 2 and Severe combined' },
      { key: 'bpSevere', label: 'Severe (>180/120)', description: 'Systolic >180 and/or diastolic >120' },
      { key: 'bpMissingDiastolic', label: 'BP Missing Diastolic', description: 'Tested members with no diastolic value (categorized by systolic only)' },
      { key: 'bpStratification', label: 'BP Stratification', description: 'Separate ACC/AHA categories', isBreakdown: true },
      { key: 'bpByAge', label: 'BP by Age Group', description: 'BP categories by age group', isBreakdown: true },
      { key: 'bpBySex', label: 'BP by Sex', description: 'BP categories by sex', isBreakdown: true },
      { key: 'bpByRace', label: 'BP by Race/Ethnicity', description: 'BP categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'bpByLocation', label: 'BP by Location', description: 'BP categories by county (from ZIP)', isBreakdown: true },
      { key: 'bpByEvent', label: 'BP by Event', description: 'BP categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'Total Cholesterol Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'Total Cholesterol Analysis',
      description: 'NCEP ATP-III total cholesterol categories using each person\'s most recent valid result.',
      guidelines: 'Desirable: < 200 mg/dL | Borderline High: 200–239 | High: ≥ 240 mg/dL',
      metrics: [
        { name: 'Total with Results', detail: 'Members with a total cholesterol value on file (the denominator).' },
        { name: 'Stratification', detail: 'Separate distribution across the 3 categories, plus breakdowns by age, sex, race/ethnicity, location and event.' }
      ]
    },
    metrics: [
      { key: 'cholTested', label: 'Total with Cholesterol Results', description: 'Members with a total cholesterol result' },
      { key: 'cholBorderlinePlus', label: 'Cholesterol ≥200', description: 'Overlapping threshold: Borderline High and High' },
      { key: 'cholHigh', label: 'Cholesterol ≥240', description: 'Members with total cholesterol ≥ 240 mg/dL' },
      { key: 'cholStratification', label: 'Cholesterol Stratification', description: 'Distribution by NCEP categories', isBreakdown: true },
      { key: 'cholByAge', label: 'Cholesterol by Age Group', description: 'Categories by age group', isBreakdown: true },
      { key: 'cholBySex', label: 'Cholesterol by Sex', description: 'Categories by sex', isBreakdown: true },
      { key: 'cholByRace', label: 'Cholesterol by Race/Ethnicity', description: 'Categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'cholByLocation', label: 'Cholesterol by Location', description: 'Categories by county (from ZIP)', isBreakdown: true },
      { key: 'cholByEvent', label: 'Cholesterol by Event', description: 'Categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'LDL-C Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'LDL-C Analysis',
      description: 'Detailed LDL cholesterol ("bad cholesterol") analysis using standard clinical categories.',
      guidelines: 'Optimal: < 100 mg/dL | Near-optimal: 100-129 | Borderline High: 130-159 | High: 160-189 | Very High: ≥ 190 mg/dL',
      metrics: [
        { name: 'Total with LDL-C Results', detail: 'Number of members who have LDL-C test values on file.' },
        { name: 'Threshold Counts', detail: 'Members with LDL-C at or above specific cutpoints (≥100, ≥160, ≥190 mg/dL).' },
        { name: 'Stratification', detail: 'Full distribution across the 5 standard LDL-C categories listed above.' },
        { name: 'By Age / By Sex', detail: 'LDL-C stratification broken down within each age group and sex for population analysis.' }
      ]
    },
    metrics: [
      { key: 'ldlTested', label: 'Total with LDL-C Results', description: 'Number of individuals with LDL-C test results' },
      { key: 'ldlAbove100', label: 'LDL-C ≥100', description: 'Individuals with LDL-C ≥ 100 mg/dL' },
      { key: 'ldlAbove160', label: 'LDL-C ≥160', description: 'Individuals with LDL-C ≥ 160 mg/dL' },
      { key: 'ldlAbove190', label: 'LDL-C ≥190', description: 'Individuals with LDL-C ≥ 190 mg/dL' },
      { key: 'ldlStratification', label: 'LDL-C Stratification', description: 'Distribution by standard LDL-C categories', isBreakdown: true },
      { key: 'ldlByAge', label: 'LDL-C by Age Group', description: 'LDL-C tested individuals by age group', isBreakdown: true },
      { key: 'ldlBySex', label: 'LDL-C by Sex', description: 'LDL-C tested individuals by sex', isBreakdown: true },
      { key: 'ldlByRace', label: 'LDL-C by Race/Ethnicity', description: 'LDL-C categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'ldlByLocation', label: 'LDL-C by Location', description: 'LDL-C categories by county (from ZIP)', isBreakdown: true },
      { key: 'ldlByEvent', label: 'LDL-C by Event', description: 'LDL-C categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'A1C Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'A1C Analysis',
      description: 'Hemoglobin A1C reflects average blood sugar over the past 2–3 months and is the standard marker for diabetes diagnosis and control.',
      guidelines: 'Normal: < 5.7% | Prediabetes: 5.7–6.4% | Diabetes: 6.5–8.9% | Uncontrolled: ≥ 9% (ADA Guidelines)',
      metrics: [
        { name: 'Total with A1C Results', detail: 'Number of members who have an A1C test value on file.' },
        { name: 'Threshold Counts', detail: 'Members with A1C ≥ 5.7% (prediabetes+), ≥ 6.5% (diabetic+), and ≥ 9% (uncontrolled).' },
        { name: 'Stratification', detail: 'Full distribution across the 4 ADA A1C categories.' },
        { name: 'By Age / By Sex', detail: 'A1C distribution within each age group and sex for population analysis.' }
      ]
    },
    metrics: [
      { key: 'a1cTested', label: 'Total with A1C Results', description: 'Number of individuals with A1C test results' },
      { key: 'a1cPrediabeticPlus', label: 'A1C ≥5.7%', description: 'Individuals at prediabetes or higher' },
      { key: 'a1cDiabeticPlus', label: 'A1C ≥6.5%', description: 'Individuals at diabetic threshold or higher' },
      { key: 'a1cUncontrolled', label: 'A1C ≥9%', description: 'Individuals with uncontrolled diabetes' },
      { key: 'a1cStratification', label: 'A1C Stratification', description: 'Distribution by ADA categories', isBreakdown: true },
      { key: 'a1cByAge', label: 'A1C by Age Group', description: 'A1C distribution by age group', isBreakdown: true },
      { key: 'a1cBySex', label: 'A1C by Sex', description: 'A1C distribution by sex', isBreakdown: true },
      { key: 'a1cByRace', label: 'A1C by Race/Ethnicity', description: 'A1C categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'a1cByLocation', label: 'A1C by Location', description: 'A1C categories by county (from ZIP)', isBreakdown: true },
      { key: 'a1cByEvent', label: 'A1C by Event', description: 'A1C categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'HDL Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'HDL Cholesterol Analysis',
      description: '"Good" cholesterol that helps remove other cholesterol from arteries. Sex-specific thresholds apply because women naturally carry higher HDL.',
      guidelines: 'Low Risk — Men: < 40 mg/dL | Women: < 50 mg/dL | Normal: between thresholds | Optimal (cardioprotective): ≥ 60 mg/dL (NCEP ATP-III)',
      metrics: [
        { name: 'Total with HDL Results', detail: 'Members with an HDL cholesterol value on file.' },
        { name: 'Low (Risk)', detail: 'Members below the sex-specific HDL threshold (Men <40, Women <50).' },
        { name: 'Optimal', detail: 'Members with HDL ≥ 60 mg/dL (cardioprotective).' },
        { name: 'Stratification / By Age / By Sex', detail: 'Distribution across Low / Normal / Optimal categories using sex-specific cutpoints.' }
      ]
    },
    metrics: [
      { key: 'hdlTested', label: 'Total with HDL Results', description: 'Number of individuals with HDL test results' },
      { key: 'hdlLowRisk', label: 'HDL Low (Risk)', description: 'Below sex-specific threshold (M <40, F <50)' },
      { key: 'hdlOptimal', label: 'HDL Optimal (≥60)', description: 'Cardioprotective HDL ≥ 60 mg/dL' },
      { key: 'hdlStratification', label: 'HDL Stratification', description: 'Distribution by Low / Normal / Optimal', isBreakdown: true },
      { key: 'hdlByAge', label: 'HDL by Age Group', description: 'HDL distribution by age group', isBreakdown: true },
      { key: 'hdlBySex', label: 'HDL by Sex', description: 'HDL distribution by sex', isBreakdown: true },
      { key: 'hdlByRace', label: 'HDL by Race/Ethnicity', description: 'HDL categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'hdlByLocation', label: 'HDL by Location', description: 'HDL categories by county (from ZIP)', isBreakdown: true },
      { key: 'hdlByEvent', label: 'HDL by Event', description: 'HDL categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'Triglycerides Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'Triglycerides Analysis',
      description: 'Type of fat carried in the blood; elevated triglycerides increase cardiovascular and pancreatic risk.',
      guidelines: 'Normal: < 150 mg/dL | Borderline High: 150–199 | High: 200–499 | Very High: ≥ 500 mg/dL (NCEP ATP-III)',
      metrics: [
        { name: 'Total with Triglyceride Results', detail: 'Members with a triglyceride value on file.' },
        { name: 'Threshold Counts', detail: 'Members at borderline+ (≥150), high+ (≥200), and very high (≥500) levels.' },
        { name: 'Stratification / By Age / By Sex', detail: 'Distribution across the 4 NCEP triglyceride categories.' }
      ]
    },
    metrics: [
      { key: 'trigTested', label: 'Total with Triglyceride Results', description: 'Number of individuals with triglyceride test results' },
      { key: 'trigBorderlinePlus', label: 'Triglycerides ≥150', description: 'Borderline high or above' },
      { key: 'trigHighPlus', label: 'Triglycerides ≥200', description: 'High or very high' },
      { key: 'trigVeryHigh', label: 'Triglycerides ≥500', description: 'Very high (pancreatitis risk)' },
      { key: 'trigStratification', label: 'Triglycerides Stratification', description: 'Distribution by NCEP categories', isBreakdown: true },
      { key: 'trigByAge', label: 'Triglycerides by Age Group', description: 'Triglyceride distribution by age group', isBreakdown: true },
      { key: 'trigBySex', label: 'Triglycerides by Sex', description: 'Triglyceride distribution by sex', isBreakdown: true },
      { key: 'trigByRace', label: 'Triglycerides by Race/Ethnicity', description: 'Triglyceride categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'trigByLocation', label: 'Triglycerides by Location', description: 'Triglyceride categories by county (from ZIP)', isBreakdown: true },
      { key: 'trigByEvent', label: 'Triglycerides by Event', description: 'Triglyceride categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'eGFR / Kidney Function',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'eGFR / Kidney Function Analysis',
      description: 'Estimated Glomerular Filtration Rate measures how well the kidneys filter blood. Reduced eGFR is a key indicator of chronic kidney disease (CKD), which sharply increases cardiovascular risk.',
      guidelines: 'Normal: ≥ 90 | Mild reduction: 60–89 | Stage 3a: 45–59 | Stage 3b: 30–44 | Stage 4: 15–29 | Stage 5 (kidney failure): < 15 mL/min/1.73m² (KDIGO)',
      metrics: [
        { name: 'Avg eGFR', detail: 'Average eGFR across tested members.' },
        { name: 'Total with eGFR Results', detail: 'Members with an eGFR value on file.' },
        { name: 'Reduced Function', detail: 'Members with eGFR < 90 (any reduction).' },
        { name: 'CKD Stage 3+', detail: 'Members with eGFR < 60 (clinically significant CKD).' },
        { name: 'Stratification / By Age / By Sex', detail: 'Distribution across the 6 KDIGO CKD stages.' }
      ]
    },
    metrics: [
      { key: 'avgEGFR', label: 'Avg eGFR', description: 'Average eGFR (mL/min/1.73m²)', suffix: ' mL/min' },
      { key: 'egfrTested', label: 'Total with eGFR Results', description: 'Number of individuals with eGFR test results' },
      { key: 'egfrReducedFunction', label: 'eGFR <90 (Any Reduction)', description: 'Below normal kidney function' },
      { key: 'egfrCKDStage3Plus', label: 'eGFR <60 (CKD Stage 3+)', description: 'Clinically significant chronic kidney disease' },
      { key: 'egfrStratification', label: 'eGFR Stratification', description: 'Distribution by KDIGO CKD stages', isBreakdown: true },
      { key: 'egfrByAge', label: 'eGFR by Age Group', description: 'eGFR distribution by age group', isBreakdown: true },
      { key: 'egfrBySex', label: 'eGFR by Sex', description: 'eGFR distribution by sex', isBreakdown: true },
      { key: 'egfrByRace', label: 'eGFR by Race/Ethnicity', description: 'CKD stages within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'egfrByLocation', label: 'eGFR by Location', description: 'CKD stages by county (from ZIP)', isBreakdown: true },
      { key: 'egfrByEvent', label: 'eGFR by Event', description: 'CKD stages by event', isBreakdown: true }
    ]
  },
  {
    category: 'Lp(a) Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'Lp(a) Analysis',
      description: 'Lipoprotein(a) is an independent, largely genetic cardiovascular risk factor. Unlike LDL, it cannot be significantly lowered through diet or exercise.',
      guidelines: 'Normal: < 75 nmol/L | Intermediate: 75-124 nmol/L | High Risk: ≥ 125 nmol/L (NLA 2024 Guidelines)',
      metrics: [
        { name: 'Total with Lp(a) Results', detail: 'Members who have Lp(a) test values recorded.' },
        { name: 'Threshold Counts', detail: 'Members with Lp(a) ≥ 75 nmol/L (intermediate+) and ≥ 125 nmol/L (high risk).' },
        { name: 'Stratification', detail: 'Distribution across the three NLA 2024 risk categories.' },
        { name: 'By Age / By Sex', detail: 'Lp(a) risk distribution within each age group and sex.' }
      ]
    },
    metrics: [
      { key: 'lpaTested', label: 'Total with Lp(a) Results', description: 'Number of individuals with Lp(a) test results' },
      { key: 'lpaAbove75', label: 'Lp(a) ≥75 nmol/L', description: 'Individuals with Lp(a) ≥ 75 nmol/L (intermediate+ risk)' },
      { key: 'lpaAbove125', label: 'Lp(a) ≥125 nmol/L', description: 'Individuals with Lp(a) ≥ 125 nmol/L (high risk)' },
      { key: 'lpaStratification', label: 'Lp(a) Stratification', description: 'Distribution by NLA 2024 risk categories', isBreakdown: true },
      { key: 'lpaByAge', label: 'Lp(a) by Age Group', description: 'Lp(a) tested individuals by age group', isBreakdown: true },
      { key: 'lpaBySex', label: 'Lp(a) by Sex', description: 'Lp(a) tested individuals by sex', isBreakdown: true },
      { key: 'lpaByRace', label: 'Lp(a) by Race/Ethnicity', description: 'Lp(a) risk categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'lpaByLocation', label: 'Lp(a) by Location', description: 'Lp(a) risk categories by county (from ZIP)', isBreakdown: true },
      { key: 'lpaByEvent', label: 'Lp(a) by Event', description: 'Lp(a) risk categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'PSA Analysis',
    group: 'Lab Analysis',
    icon: <FontAwesomeIcon icon={faFlask} />,
    modalContent: {
      title: 'PSA Analysis',
      description: 'Prostate-Specific Antigen (PSA) is a protein produced by the prostate gland. Elevated levels may indicate prostate conditions requiring further evaluation.',
      guidelines: 'Normal: < 4 ng/mL | Intermediate: 4-10 ng/mL | High Risk: ≥ 10 ng/mL',
      note: 'Age-adjusted normal ranges: 40-49: 0-2.5 | 50-59: 0-3.5 | 60-69: 0-4.5 | 70+: 0-6.5 ng/mL',
      metrics: [
        { name: 'Total with PSA Results', detail: 'Members who have PSA test values recorded.' },
        { name: 'Threshold Counts', detail: 'Members with PSA ≥ 4 ng/mL (intermediate+) and ≥ 10 ng/mL (high risk).' },
        { name: 'Stratification', detail: 'Distribution across the three risk categories.' },
        { name: 'By Age / By Sex', detail: 'PSA levels broken down by age group and sex.' }
      ]
    },
    metrics: [
      { key: 'psaTested', label: 'Total with PSA Results', description: 'Number of individuals with PSA test results' },
      { key: 'psaAbove4', label: 'PSA ≥4 ng/mL', description: 'Individuals with PSA ≥ 4 ng/mL (intermediate+ risk)' },
      { key: 'psaAbove10', label: 'PSA ≥10 ng/mL', description: 'Individuals with PSA ≥ 10 ng/mL (high risk)' },
      { key: 'psaStratification', label: 'PSA Stratification', description: 'Distribution by risk categories (Normal <4, Intermediate 4-10, High ≥10)', isBreakdown: true },
      { key: 'psaByAge', label: 'PSA by Age Group', description: 'PSA tested individuals by age group', isBreakdown: true },
      { key: 'psaBySex', label: 'PSA by Sex', description: 'PSA tested individuals by sex', isBreakdown: true },
      { key: 'psaByRace', label: 'PSA by Race/Ethnicity', description: 'PSA risk categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'psaByLocation', label: 'PSA by Location', description: 'PSA risk categories by county (from ZIP)', isBreakdown: true },
      { key: 'psaByEvent', label: 'PSA by Event', description: 'PSA risk categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'WHtR Analysis',
    group: 'Body Composition',
    icon: <FontAwesomeIcon icon={faWeightScale} />,
    modalContent: {
      title: 'WHtR Analysis',
      description: 'Waist-to-Height Ratio (WHtR) measures central obesity — abdominal fat relative to height. It is a simple, strong predictor of cardiometabolic risk and works across sexes with a single cutpoint.',
      guidelines: 'Normal: < 0.50 | Moderate (increased): 0.50–0.59 | High: ≥ 0.60\nGeneral rule: “keep your waist to less than half your height.”',
      metrics: [
        { name: 'Total with WHtR Data', detail: 'Members with both waist and height measurements recorded, used to calculate WHtR.' },
        { name: 'WHtR High Risk', detail: 'Members whose WHtR is ≥ 0.60 (high risk).' },
        { name: 'Stratification', detail: 'Distribution across Normal, Moderate, and High risk categories using the 0.50 / 0.60 cutpoints.' },
        { name: 'By Age / By Sex', detail: 'WHtR risk distribution within each age group and sex for targeted intervention planning.' }
      ]
    },
    metrics: [
      { key: 'whrTested', label: 'Total with WHtR Data', description: 'Number of individuals with waist-to-height ratio measurements' },
      { key: 'whrHighRisk', label: 'WHtR High Risk', description: 'Individuals with high-risk WHtR (≥ 0.60)' },
      { key: 'whrStratification', label: 'WHtR Stratification', description: 'Distribution by risk categories (Normal, Moderate, High)', isBreakdown: true },
      { key: 'whrByAge', label: 'WHtR by Age Group', description: 'WHtR risk distribution by age group', isBreakdown: true },
      { key: 'whrBySex', label: 'WHtR by Sex', description: 'WHtR risk distribution by sex', isBreakdown: true },
      { key: 'whrByRace', label: 'WHtR by Race/Ethnicity', description: 'WHtR risk categories within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'whrByLocation', label: 'WHtR by Location', description: 'WHtR risk categories by county (from ZIP)', isBreakdown: true },
      { key: 'whrByEvent', label: 'WHtR by Event', description: 'WHtR risk categories by event', isBreakdown: true }
    ]
  },
  {
    category: 'Visceral Fat Risk',
    group: 'Body Composition',
    icon: <FontAwesomeIcon icon={faWeightScale} />,
    modalContent: {
      title: 'Visceral Fat Risk',
      description: 'Visceral fat surrounds internal organs (liver, intestines, kidneys) and is strongly linked to cardiovascular disease, type 2 diabetes, and metabolic syndrome. This report uses the user-entered visceral fat rating from a BIA (bioelectrical impedance) body composition scale.',
      guidelines: '1–9: Normal  |  10–14: High  |  15+: Very High / Excess Visceral Fat',
      note: 'Visceral fat ratings are captured via a BIA device such as the Oxiline Scale MD Pro and entered on the member\u2019s Medical Testing tab.',
      metrics: [
        { name: 'Total Assessed', detail: 'Members with a recorded visceral fat rating.' },
        { name: 'High Risk', detail: 'Members with a visceral fat rating of 10 or higher (High or Very High).' },
        { name: 'Stratification', detail: 'Distribution across Normal, High, and Very High categories.' },
        { name: 'By Age / By Sex', detail: 'Risk distribution within each demographic group.' }
      ]
    },
    metrics: [
      { key: 'visceralAssessed', label: 'Total Assessed', description: 'Individuals with a recorded visceral fat rating' },
      { key: 'visceralHighRisk', label: 'High Risk', description: 'Individuals with visceral fat rating \u226510 (High or Very High)' },
      { key: 'visceralStratification', label: 'Risk Stratification', description: 'Distribution by visceral fat rating (Normal 1\u20139, High 10\u201314, Very High 15+)', isBreakdown: true },
      { key: 'visceralByAge', label: 'Risk by Age Group', description: 'Visceral fat risk distribution by age group', isBreakdown: true },
      { key: 'visceralBySex', label: 'Risk by Sex', description: 'Visceral fat risk distribution by sex', isBreakdown: true },
      { key: 'visceralByRace', label: 'Risk by Race/Ethnicity', description: 'Visceral fat risk within each race/ethnicity (tested denominator per group)', isBreakdown: true },
      { key: 'visceralByLocation', label: 'Risk by Location', description: 'Visceral fat risk by county (from ZIP)', isBreakdown: true },
      { key: 'visceralByEvent', label: 'Risk by Event', description: 'Visceral fat risk by event', isBreakdown: true }
    ]
  },
  {
    category: 'Operations',
    group: 'Event & Operations',
    icon: <FontAwesomeIcon icon={faClock} />,
    modalContent: {
      title: 'Operations',
      description: 'Operational efficiency metrics for screening workflow optimization.',
      metrics: [
        { name: 'Avg Wait Time', detail: 'Average time (in minutes) from when a member submits their intake form to when screening begins.' },
        { name: 'Avg Screening Time', detail: 'Average duration (in minutes) of the screening process from start to completion.' },
        { name: 'No-Show Rate', detail: 'Percentage of registered members who did not show up for their screening.' }
      ]
    },
    metrics: [
      { key: 'avgWaitTime', label: 'Avg Wait Time', description: 'Average form-to-intake wait time (minutes)', suffix: ' min' },
      { key: 'avgScreeningTime', label: 'Avg Screening Time', description: 'Average screening duration (minutes)', suffix: ' min' },
      { key: 'noShowRate', label: 'No-Show Rate', description: 'Percentage of no-shows', suffix: '%' }
    ]
  },
  {
    category: 'Members',
    group: 'Event & Operations',
    icon: <FontAwesomeIcon icon={faUserCheck} />,
    modalContent: {
      title: 'Members',
      description: 'Member-level participation metrics.',
      metrics: [
        { name: 'Unique Members', detail: 'Count of distinct individuals screened (deduplicated by member ID).' },
        { name: 'Repeat Visitors', detail: 'Members who have been screened more than once across all events.' },
        { name: 'Walk-In Screenings', detail: 'Screenings from members who were not pre-registered — they walked in on the day of the event.' }
      ]
    },
    metrics: [
      { key: 'uniqueMembers', label: 'Unique Members', description: 'Distinct members screened' },
      { key: 'repeatVisitors', label: 'Repeat Visitors', description: 'Members with more than one visit' },
      { key: 'walkInScreenings', label: 'Walk-In Screenings', description: 'Screenings from walk-in members' }
    ]
  }
];

// Flatten for quick lookup
const ALL_METRICS = METRIC_CATEGORIES.flatMap(c => c.metrics);

// Age-band grouping. Each band is an inclusive [min, max] range; max === null
// means "and older". The default preserves the historical grouping used by the
// backend. Presets give one-click common groupings; users can also edit freely.
const DEFAULT_AGE_BANDS = [
  { label: '18-30', min: 18, max: 30 },
  { label: '31-45', min: 31, max: 45 },
  { label: '46-60', min: 46, max: 60 },
  { label: '61-75', min: 61, max: 75 },
  { label: '75+', min: 76, max: null }
];

const AGE_BAND_PRESETS = [
  { key: 'default', label: 'Default (5 groups)', bands: DEFAULT_AGE_BANDS },
  {
    key: 'adult3',
    label: 'Adult (18-44 / 45-64 / 65+)',
    bands: [
      { label: '18-44', min: 18, max: 44 },
      { label: '45-64', min: 45, max: 64 },
      { label: '65+', min: 65, max: null }
    ]
  },
  {
    key: 'withMinors',
    label: 'With minors (0-17 / 18-44 / 45-64 / 65+)',
    bands: [
      { label: '0-17', min: 0, max: 17 },
      { label: '18-44', min: 18, max: 44 },
      { label: '45-64', min: 45, max: 64 },
      { label: '65+', min: 65, max: null }
    ]
  },
  {
    key: 'decades',
    label: 'By decade (18-29 / 30s / 40s / 50s / 60s / 70+)',
    bands: [
      { label: '18-29', min: 18, max: 29 },
      { label: '30-39', min: 30, max: 39 },
      { label: '40-49', min: 40, max: 49 },
      { label: '50-59', min: 50, max: 59 },
      { label: '60-69', min: 60, max: 69 },
      { label: '70+', min: 70, max: null }
    ]
  }
];

// Structural equality check for two band arrays (order-sensitive on min/max).
const ageBandsEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((band, i) => band.min === b[i].min && (band.max ?? null) === (b[i].max ?? null));
};

// Derive a readable label from a band's numeric range.
const bandLabel = (min, max) => (max === null || max === undefined || max === '') ? `${min}+` : `${min}-${max}`;

const SECTION_GROUPS = [
  { key: 'Event & Operations', label: 'Event & Operations', icon: <FontAwesomeIcon icon={faCalendarAlt} /> },
  { key: 'Demographics', label: 'Demographics', icon: <FontAwesomeIcon icon={faUsers} /> },
  { key: 'Clinical Outcomes', label: 'Clinical Outcomes', icon: <FontAwesomeIcon icon={faHeartPulse} /> },
  { key: 'Lab Analysis', label: 'Lab Analysis', icon: <FontAwesomeIcon icon={faFlask} /> },
  { key: 'Body Composition', label: 'Body Composition', icon: <FontAwesomeIcon icon={faWeightScale} /> }
];

const REPORT_TEMPLATES = [
  {
    key: 'full',
    label: 'Full Health Report',
    description: 'All health, lab, and body composition metrics',
    metrics: ALL_METRICS.filter(m => {
      const cat = METRIC_CATEGORIES.find(c => c.metrics.some(cm => cm.key === m.key));
      return cat && ['Clinical Outcomes', 'Lab Analysis', 'Body Composition'].includes(cat.group);
    }).map(m => m.key)
  },
  {
    key: 'lipid',
    label: 'Lipid Panel',
    description: 'LDL-C, Lp(a), HDL, triglyceride, and cholesterol analysis',
    metrics: ['avgCholesterol', 'highCholesterolCount', 'avgHDL', 'avgLDL', 'avgTriglycerides', 'avgLPA', 'elevatedLPACount', 'elevatedLPAPercent',
      'ldlTested', 'ldlAbove100', 'ldlAbove160', 'ldlAbove190', 'ldlStratification', 'ldlByAge', 'ldlBySex',
      'hdlTested', 'hdlLowRisk', 'hdlOptimal', 'hdlStratification', 'hdlByAge', 'hdlBySex',
      'trigTested', 'trigBorderlinePlus', 'trigHighPlus', 'trigVeryHigh', 'trigStratification', 'trigByAge', 'trigBySex',
      'lpaTested', 'lpaAbove75', 'lpaAbove125', 'lpaStratification', 'lpaByAge', 'lpaBySex']
  },
  {
    key: 'metabolic-renal',
    label: 'Diabetes & Kidney Function',
    description: 'A1C, eGFR / CKD staging, and glucose metrics',
    metrics: ['avgGlucose', 'avgA1C',
      'a1cTested', 'a1cPrediabeticPlus', 'a1cDiabeticPlus', 'a1cUncontrolled', 'a1cStratification', 'a1cByAge', 'a1cBySex',
      'avgEGFR', 'egfrTested', 'egfrReducedFunction', 'egfrCKDStage3Plus', 'egfrStratification', 'egfrByAge', 'egfrBySex']
  },
  {
    key: 'demographics',
    label: 'Demographics Report',
    description: 'Gender, age, and ethnicity breakdowns with member counts',
    metrics: ['males', 'females', 'other', 'malePercent', 'femalePercent', 'otherPercent', 'ageBreakdown', 'ethnicityBreakdown', 'uniqueMembers', 'repeatVisitors']
  },
  {
    key: 'cardiovascular',
    label: 'Cardiovascular Risk',
    description: 'Blood pressure, risk scores, WHR, and visceral fat analysis',
    metrics: ['avgSystolic', 'avgDiastolic', 'highBPCount', 'highBPPercent', 'highRiskMembers', 'criticalRiskMembers', 'avgRiskScore', 'riskBreakdown',
      'whrTested', 'whrHighRisk', 'whrStratification', 'whrByAge', 'whrBySex',
      'visceralAssessed', 'visceralHighRisk', 'visceralStratification', 'visceralByAge', 'visceralBySex']
  },
  {
    key: 'screening-ops',
    label: 'Screening Operations',
    description: 'Event throughput, wait times, no-shows, and booking conversion',
    metrics: ['totalEvents', 'avgScreeningsPerEvent', 'totalScreenings', 'completedScreenings', 'noShows',
      'avgWaitTime', 'avgScreeningTime', 'noShowRate', 'totalBookings', 'cancelledBookings', 'bookingConversion',
      'uniqueMembers', 'repeatVisitors', 'walkInScreenings']
  }
];

// Race/ethnicity options — must mirror the canonical list used in
// FormConfig.js on the backend. The `value` is the raw stored value (sent to
// backend cohort filter); the `label` is what we show in the dropdown.
const RACE_OPTIONS = [
  { value: 'blackOrAfricanAmerican', label: 'Black or African American' },
  { value: 'white', label: 'White' },
  { value: 'hispanicOrLatino', label: 'Hispanic or Latino' },
  { value: 'asianOrAsianAmerican', label: 'Asian or Asian American' },
  { value: 'americanIndianOrAlaskaNative', label: 'American Indian or Alaska Native' },
  { value: 'middleEasternOrNorthAfrican', label: 'Middle Eastern or North African' },
  { value: 'nativeHawaiianOrOPI', label: 'Native Hawaiian or Other Pacific Islander' },
  { value: 'other', label: 'Other' }
];
const raceLabel = (val) => RACE_OPTIONS.find(o => o.value === val)?.label || val;

// Initial filter state read from the URL query string (once, on page load).
// Lets users refresh / share a link without losing their report setup.
// Read the deep-link query string. Called once per MOUNT (not at module load):
// this page is imported eagerly, so a module-level snapshot would freeze
// whatever URL the app booted on and re-apply stale filters on later visits.
const readUrlParams = () => {
  try {
    return Object.fromEntries(new URLSearchParams(window.location.search));
  } catch {
    return {};
  }
};

const FilterBar = styled.div`
  background: #fff;
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 16px 20px;
  margin-bottom: 24px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  &:not(:last-child) {
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${COLORS.border};
  }
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMedium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  min-width: 52px;
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 28px;
  background: ${COLORS.border};
  margin: 0 2px;
`;

const ReportCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;

  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border};
    padding: 12px 20px;
    min-height: auto;
  }

  .ant-card-body {
    padding: 20px;
  }
`;

const NarrativeCard = styled.div`
  background: #fff;
  border-radius: 12px;
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 20px;
  margin-top: 20px;
`;

const NarrativeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .ant-select {
    min-width: 220px;
  }

  .ant-select-single .ant-select-selector {
    height: 36px !important;
    display: flex;
    align-items: center;
    font-size: 14px;
  }

  .ant-btn {
    height: 36px;
    font-size: 14px;
  }

  .ant-select-single .ant-select-selection-item,
  .ant-select-single .ant-select-selection-placeholder {
    line-height: 30px !important;
  }

  .ant-btn {
    height: 32px;
    display: inline-flex;
    align-items: center;
  }
`;

const NarrativeStyleHints = styled.div`
  font-size: 12px;
  color: ${COLORS.textMuted};
  margin-bottom: 14px;
  font-style: italic;
`;

const NarrativeOutput = styled.div`
  background: #f9fafb;
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  line-height: 1.75;
  color: ${COLORS.textDark};
  max-height: 500px;
  overflow-y: auto;
`;

const NarrativeSectionTitle = styled.div`
  font-weight: 700;
  color: ${COLORS.textDark};
  margin-top: 12px;
  margin-bottom: 6px;

  &:first-child {
    margin-top: 0;
  }
`;

const NarrativeBulletLine = styled.div`
  margin-bottom: 8px;
`;

const NarrativeParagraphLine = styled.div`
  margin-bottom: 10px;
`;

const NarrativeCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 16px;
  background: ${COLORS.$primaryBlue};
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: blink 1s step-end infinite;
  @keyframes blink {
    50% { opacity: 0; }
  }
`;

const MetricSelectorCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 100%;

  .ant-card-body {
    padding: 16px 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${COLORS.border};
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textDark};

  .anticon, svg:not(.ant-select-suffix svg) {
    font-size: 16px;
    width: 16px;
    height: 16px;
    color: ${COLORS.$primaryBlue};
  }
`;

const MetricCheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  align-content: flex-start;
`;

const MetricCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${props => props.$selected ? COLORS.$primaryBlue : COLORS.border};
  background: ${props => props.$selected ? '#eef4ff' : '#fff'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  user-select: none;

  &:hover {
    border-color: ${COLORS.$primaryBlue};
    background: ${props => props.$selected ? '#eef4ff' : '#f6f9ff'};
  }
`;

const SectionGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  margin-top: ${props => props.$first ? '0' : '8px'};
  cursor: pointer;
  user-select: none;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid ${COLORS.border};

  &:hover {
    background: #f0f4f8;
  }

  .anticon, svg {
    font-size: 15px;
    width: 15px;
    height: 15px;
    color: ${COLORS.$primaryBlue};
  }
`;

const SectionGroupLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textDark};
  flex: 1;
`;

const SectionGroupCount = styled.span`
  font-size: 11px;
  color: ${COLORS.textMuted};
  font-weight: 400;
`;

const CollapsibleContent = styled.div`
  display: grid;
  grid-template-rows: ${props => props.$collapsed ? '0fr' : '1fr'};
  transition: grid-template-rows 0.3s ease;
  > div {
    overflow: hidden;
  }
`;

const TemplateBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const TemplateChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${props => props.$active ? COLORS.$primaryBlue : COLORS.border};
  background: ${props => props.$active ? '#eef4ff' : '#fff'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.$active ? COLORS.$primaryBlue : COLORS.textMedium};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.$primaryBlue};
    color: ${COLORS.$primaryBlue};
  }
`;

const ResultStatCard = styled(Card)`
  border-radius: 10px;
  border: 1px solid ${COLORS.border};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 100%;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  .ant-card-body {
    padding: 16px 20px;
  }

  .ant-statistic-title {
    font-size: 12px;
    color: ${COLORS.textMedium};
    margin-bottom: 4px;
  }

  .ant-statistic-content {
    font-size: 24px;
    font-weight: 700;
  }
`;

const BreakdownTable = styled.div`
  border-radius: 10px;
  border: 1px solid ${COLORS.border};
  /* Wide strata cross-tabs (many columns) scroll inside the card instead of
     spilling past the page edge. */
  overflow-x: auto;
  overflow-y: hidden;
  margin-bottom: 8px;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid ${COLORS.border};
  font-size: 13px;
  gap: 8px;
  /* Match the widest row so header/body cells stay aligned when scrolling. */
  width: max-content;
  min-width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

const BarFill = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${COLORS.$primaryBlue};
  width: ${props => props.$width || 0}%;
  transition: width 0.3s ease;
`;

const SamplingBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 12.5px;
  background: ${props => props.$variant === 'warn' ? '#fff7e6' : '#e6f4ff'};
  border: 1px solid ${props => props.$variant === 'warn' ? '#ffd591' : '#91caff'};
  color: #444;
`;

const MembersSection = styled.div`
  margin-top: 28px;
  border-top: 1px solid ${COLORS.border};
  padding-top: 20px;
`;

const MembersHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 12px;
  flex-wrap: wrap;
`;

const MembersTable = styled.div`
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  font-size: 12.5px;
  /* Horizontal scroll kicks in when the user picks more spreadsheet columns
     than fit in the viewport. Vertical clipping is handled by overflow on
     the row container itself (rows are full-width children). */
  overflow-x: auto;
  overflow-y: hidden;
`;

const MemberRow = styled.div`
  display: grid;
  /* FIXED track widths (no fr units). Every row resolves to the exact same
     column grid regardless of its own content, so header + body always line
     up, and the table's parent (overflow-x: auto) scrolls horizontally when
     the columns exceed the viewport instead of rows bleeding off the page. */
  grid-template-columns:
    180px
    52px
    48px
    repeat(${p => p.$valueCols || 4}, 112px)
    240px;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 1px solid ${COLORS.border};
  align-items: center;
  /* All rows share identical fixed tracks, so max-content is identical for
     every row — keeping them flush while the container scrolls. */
  width: max-content;
  min-width: 100%;

  &:last-child { border-bottom: none; }

  &.header {
    background: #fafafa;
    font-weight: 600;
    color: #666;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  /* Clip long values (emails, event names) inside their fixed column instead
     of pushing neighbors out of alignment. Flags column keeps wrapping chips. */
  & > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > span:last-child {
    white-space: normal;
    overflow: visible;
  }

  .num { text-align: right; font-variant-numeric: tabular-nums; }
`;

const FlagChip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
  margin: 0 3px 3px 0;
  background: ${p => p.$severity === 'high' ? '#fff1f0' : p.$severity === 'medium' ? '#fffbe6' : '#f0f0f0'};
  color: ${p => p.$severity === 'high' ? '#cf1322' : p.$severity === 'medium' ? '#ad6800' : '#595959'};
  border: 1px solid ${p => p.$severity === 'high' ? '#ffa39e' : p.$severity === 'medium' ? '#ffe58f' : '#d9d9d9'};
`;

/* Footer shown under the members table when the list is paginated — surfaces
   the "showing N of M" count plus the reveal-more controls. */
const MembersTableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  background: #fafafa;
  border-top: 1px solid ${COLORS.border};

  .count {
    font-size: 12px;
    color: ${COLORS.textMedium};
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

/* Cohesive filter/actions toolbar shown above the members table. Groups every
   control into a labeled "field" so the row reads as tidy columns instead of a
   cramped strip of small inputs. */
const MembersToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 14px 20px;
  padding: 14px 18px;
  margin-bottom: 16px;
  background: #f7f9fb;
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
`;

const ToolbarField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  .field-label {
    font-size: 11px;
    font-weight: 600;
    color: ${COLORS.textMedium};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }

  .field-control {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
  }

  /* Push the action buttons (Columns / Export) to the far right and bottom-align
     them with the input controls. */
  &.actions {
    margin-left: auto;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
`;

const Reports = ({ dateRange: propDateRange }) => {
  const { setPageHeader } = usePageHeader();
  const [URL_INIT] = useState(readUrlParams);
  const urlList = (key) => (URL_INIT[key] ? URL_INIT[key].split(',').filter(Boolean) : []);
  // Drop unknown keys so a mistyped or outdated link can't select phantom metrics.
  const [selectedMetrics, setSelectedMetrics] = useState(() => urlList('metrics').filter(k => ALL_METRICS.some(m => m.key === k)));
  // [null, null] = all time (no date filter sent to the API).
  const [dateRange, setDateRange] = useState(() => {
    if (URL_INIT.qr === 'all') return [null, null];
    if (URL_INIT.start && URL_INIT.end) {
      const s = dayjs(URL_INIT.start);
      const e = dayjs(URL_INIT.end);
      if (s.isValid() && e.isValid()) return [s, e];
    }
    return propDateRange || [dayjs().subtract(30, 'days'), dayjs()];
  });
  const [quickRange, setQuickRange] = useState(URL_INIT.qr || (URL_INIT.start ? 'custom' : 'month'));
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [eventTypeIds, setEventTypeIds] = useState(() => urlList('ets')); // multi-select event types
  const [bookingFilter, setBookingFilter] = useState(URL_INIT.bk || null);
  const [excludeArchived, setExcludeArchived] = useState(URL_INIT.arch !== '0');
  const [memberLimit, setMemberLimit] = useState(() => {
    const n = parseInt(URL_INIT.limit, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [cohortSex, setCohortSex] = useState(URL_INIT.sex || 'all'); // 'all' | 'Male' | 'Female' | 'Other' | 'Unknown'
  const [cohortRace, setCohortRace] = useState(URL_INIT.race || 'all'); // 'all' | raw FormConfig value
  const [cohortZips, setCohortZips] = useState(() => urlList('zips')); // array of 5-digit or 3-digit-prefix ZIPs (OR-matched)
  const [cohortInsured, setCohortInsured] = useState(URL_INIT.ins || 'all'); // 'all' | 'insured' | 'uninsured'
  const [cohortStress, setCohortStress] = useState(URL_INIT.stress || 'all'); // 'all' | 'low' | 'moderate' | 'high'
  const [cohortCounties, setCohortCounties] = useState(() => urlList('counties')); // array of county FIPS (OR-matched)
  const [cohortAgeMin, setCohortAgeMin] = useState(() => {
    const n = parseInt(URL_INIT.ageMin, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  });
  const [cohortAgeMax, setCohortAgeMax] = useState(() => {
    const n = parseInt(URL_INIT.ageMax, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  });
  const [counties, setCounties] = useState([]); // [{ fips, county, state, label }]
  // Event location scope: a metro key ('dallas', 'houston') or 'city:<name>'.
  // Matches the location saved on each individual event across ALL calendars,
  // so a National Tour stop held in Dallas is included under Dallas.
  const [eventLocation, setEventLocation] = useState(URL_INIT.loc || null);
  const [locationOptions, setLocationOptions] = useState({ metros: [], cities: [] });
  const locationOptionLabel = useCallback((value) => {
    if (!value) return '';
    const metro = locationOptions.metros.find(m => m.value === value);
    if (metro) return metro.label;
    const city = locationOptions.cities.find(c => c.value === value);
    return city ? city.label : value.replace(/^city:/i, '');
  }, [locationOptions]);
  // Open the cohort panel on load when the URL carries cohort filters, so
  // shared links surface their active filters immediately.
  const [cohortPanelOpen, setCohortPanelOpen] = useState(() => Boolean(
    URL_INIT.sex || URL_INIT.race || URL_INIT.zips || URL_INIT.ins || URL_INIT.stress || URL_INIT.counties || URL_INIT.limit ||
    URL_INIT.ageMin || URL_INIT.ageMax
  ));
  // When ON, the cohort selector fills the limit with members who have data
  // for the user's selected metrics first, then falls back to recency for the
  // remaining slots. Helps charts for sparse fields (e.g. Visceral Fat) avoid
  // empty results when the most-recent N members happen not to have that test.
  const [prioritizeData, setPrioritizeData] = useState(URL_INIT.pri === '1');
  // Anchor used to scroll the user down to the results after generating.
  const resultsRef = useRef(null);
  // Snapshot of the cohort settings at last report-generation. Used to show a
  // "stale" warning when the user changes cohort inputs but hasn't re-Generated.
  const [lastGenSnapshot, setLastGenSnapshot] = useState(null);
  const [infoModal, setInfoModal] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [metricSearch, setMetricSearch] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [narrativeStyle, setNarrativeStyle] = useState('grant');
  const [narrativeText, setNarrativeText] = useState('');
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [lpaUnit, setLpaUnit] = useState('nmol'); // 'nmol' or 'mgdl'
  const [identifyMembers, setIdentifyMembers] = useState(true); // false = show initials only
  const [memberFilter, setMemberFilter] = useState('all'); // 'all' | 'highRisk' | 'mediumPlus' | 'noRisk'
  const [sexFilter, setSexFilter] = useState('all'); // 'all' | 'Male' | 'Female' | 'Other' | 'Unknown'
  const [ageRange, setAgeRange] = useState({ min: null, max: null });
  const [memberSort, setMemberSort] = useState({ field: 'risk', dir: 'desc' });
  const [membersExpanded, setMembersExpanded] = useState(true);
  // How many member rows to actually render in the DOM at once. Large cohorts
  // (thousands of members) blow up the DOM/fiber tree — rendering all of them
  // freezes the tab and overflows React's fiber walk on hot-reload. We render
  // in pages and let the user reveal more.
  const MEMBER_PAGE_SIZE = 100;
  const [memberRenderLimit, setMemberRenderLimit] = useState(MEMBER_PAGE_SIZE);
  // Collapse the render window back to the first page whenever the underlying
  // list changes (new report, or a different view filter/sort), so we never
  // try to paint thousands of rows at once after a change.
  useEffect(() => {
    setMemberRenderLimit(MEMBER_PAGE_SIZE);
  }, [reportData, memberFilter, sexFilter, ageRange, memberSort]);
  // User-customized spreadsheet columns. null = auto (derive from selectedMetrics).
  // Persisted in localStorage so the choice survives reloads.
  const SPREADSHEET_COLS_STORAGE_KEY = 'bha-reports-spreadsheet-cols-v1';
  const [spreadsheetCols, setSpreadsheetCols] = useState(() => {
    try {
      const raw = window.localStorage?.getItem(SPREADSHEET_COLS_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (spreadsheetCols && spreadsheetCols.length > 0) {
        window.localStorage?.setItem(SPREADSHEET_COLS_STORAGE_KEY, JSON.stringify(spreadsheetCols));
      } else {
        window.localStorage?.removeItem(SPREADSHEET_COLS_STORAGE_KEY);
      }
    } catch { /* ignore quota / private mode errors */ }
  }, [spreadsheetCols]);

  // Custom age-group bands for all by-age breakdowns. Persisted in localStorage.
  const AGE_BANDS_STORAGE_KEY = 'bha-reports-age-bands-v1';
  const [ageBands, setAgeBands] = useState(() => {
    try {
      const raw = window.localStorage?.getItem(AGE_BANDS_STORAGE_KEY);
      if (!raw) return DEFAULT_AGE_BANDS;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_AGE_BANDS;
    } catch {
      return DEFAULT_AGE_BANDS;
    }
  });
  useEffect(() => {
    try {
      if (ageBands && !ageBandsEqual(ageBands, DEFAULT_AGE_BANDS)) {
        window.localStorage?.setItem(AGE_BANDS_STORAGE_KEY, JSON.stringify(ageBands));
      } else {
        window.localStorage?.removeItem(AGE_BANDS_STORAGE_KEY);
      }
    } catch { /* ignore quota / private mode errors */ }
  }, [ageBands]);
  const [ageBandsOpen, setAgeBandsOpen] = useState(false);

  // Raw-age export modal state
  const [ageExportOpen, setAgeExportOpen] = useState(false);
  const [ageExportBins, setAgeExportBins] = useState('');
  const [ageExportZipMode, setAgeExportZipMode] = useState('full'); // 'full' | 'zip3'
  const [ageExportLoading, setAgeExportLoading] = useState(false);

  // Lp(a) conversion: stored as nmol/L, optionally display as mg/dL (÷ 2.5)
  const LPA_KEYS = ['avgLPA', 'elevatedLPACount', 'elevatedLPAPercent', 'lpaTested', 'lpaAbove75', 'lpaAbove125', 'lpaStratification', 'lpaByAge', 'lpaBySex', 'lpaByRace', 'lpaByLocation', 'lpaByEvent'];
  const convertLpa = (value) => {
    if (lpaUnit === 'mgdl' && typeof value === 'number') return Math.round(value / 2.5);
    return value;
  };
  const lpaUnitLabel = lpaUnit === 'nmol' ? 'nmol/L' : 'mg/dL';
  // Convert strata range labels when toggled to mg/dL
  const convertLpaRangeLabel = (label) => {
    if (lpaUnit !== 'mgdl') return label;
    return label
      .replace('<75', '<30')
      .replace('75-124', '30-49')
      .replace('≥125', '≥50');
  };

  const toggleSection = (key) => {
    setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const applyTemplate = (template) => {
    if (activeTemplate === template.key) {
      setActiveTemplate(null);
      return;
    }
    setSelectedMetrics(template.metrics);
    setActiveTemplate(template.key);
  };

  React.useEffect(() => {
    setPageHeader({ title: 'Custom Reports', count: null, countIcon: null });
  }, [setPageHeader]);

  // Load event types for filter dropdown
  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const response = await api.get('/screening/analytics/reports/event-types');
        setEventTypes(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error loading event types:', error);
      }
    };
    fetchEventTypes();
  }, []);

  // Load counties present in the population for the County cohort filter
  useEffect(() => {
    const fetchCounties = async () => {
      try {
        const response = await api.get('/screening/analytics/reports/counties');
        setCounties(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error loading counties:', error);
      }
    };
    fetchCounties();
  }, []);

  // Load metro areas + cities that appear on bookings for the Location scope
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get('/screening/analytics/reports/locations');
        setLocationOptions({
          metros: Array.isArray(response?.metros) ? response.metros : [],
          cities: Array.isArray(response?.cities) ? response.cities : []
        });
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    fetchLocations();
  }, []);

  // Keep the URL query string in sync with the current filters (replaceState,
  // no navigation) so a refresh or shared link restores the same report setup.
  useEffect(() => {
    try {
      const p = new URLSearchParams();
      if (selectedMetrics.length > 0) p.set('metrics', selectedMetrics.join(','));
      if (quickRange !== 'month') p.set('qr', quickRange);
      if (quickRange !== 'all' && dateRange[0] && dateRange[1]) {
        p.set('start', dateRange[0].format('YYYY-MM-DD'));
        p.set('end', dateRange[1].format('YYYY-MM-DD'));
      }
      if (eventTypeIds.length > 0) p.set('ets', eventTypeIds.join(','));
      if (eventLocation) p.set('loc', eventLocation);
      if (bookingFilter) p.set('bk', bookingFilter);
      if (!excludeArchived) p.set('arch', '0');
      if (memberLimit) p.set('limit', String(memberLimit));
      if (cohortSex !== 'all') p.set('sex', cohortSex);
      if (cohortRace !== 'all') p.set('race', cohortRace);
      if (cohortZips.length > 0) p.set('zips', cohortZips.join(','));
      if (cohortInsured !== 'all') p.set('ins', cohortInsured);
      if (cohortStress !== 'all') p.set('stress', cohortStress);
      if (cohortCounties.length > 0) p.set('counties', cohortCounties.join(','));
      if (cohortAgeMin != null) p.set('ageMin', String(cohortAgeMin));
      if (cohortAgeMax != null) p.set('ageMax', String(cohortAgeMax));
      if (prioritizeData) p.set('pri', '1');
      const qs = p.toString();
      window.history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
    } catch { /* history API unavailable — non-fatal */ }
  }, [selectedMetrics, quickRange, dateRange, eventTypeIds, eventLocation, bookingFilter, excludeArchived,
    memberLimit, cohortSex, cohortRace, cohortZips, cohortInsured, cohortStress, cohortCounties, cohortAgeMin, cohortAgeMax, prioritizeData]);

  // Load bookings when event type filter changes. The bookings dropdown only
  // scopes to a single event type; when multiple types are selected we load all
  // bookings (booking filter is cleared/relaxed in that case).
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const params = eventTypeIds.length === 1 ? `?eventTypeId=${eventTypeIds[0]}` : '';
        const response = await api.get(`/screening/analytics/reports/bookings${params}`);
        setBookings(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error loading bookings:', error);
      }
    };
    fetchBookings();
  }, [eventTypeIds]);

  const toggleMetric = useCallback((metricKey) => {
    setSelectedMetrics(prev =>
      prev.includes(metricKey)
        ? prev.filter(m => m !== metricKey)
        : [...prev, metricKey]
    );
    setActiveTemplate(null);
  }, []);

  const selectAllInCategory = useCallback((category) => {
    const cat = METRIC_CATEGORIES.find(c => c.category === category);
    if (!cat) return;
    const catKeys = cat.metrics.map(m => m.key);
    const allSelected = catKeys.every(k => selectedMetrics.includes(k));
    if (allSelected) {
      setSelectedMetrics(prev => prev.filter(m => !catKeys.includes(m)));
    } else {
      setSelectedMetrics(prev => [...new Set([...prev, ...catKeys])]);
    }
    setActiveTemplate(null);
  }, [selectedMetrics]);

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '7days': newRange = [now.subtract(7, 'days'), now]; break;
      case 'month': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(3, 'months'), now]; break;
      case '6months': newRange = [now.subtract(6, 'months'), now]; break;
      case 'year': newRange = [now.subtract(1, 'year'), now]; break;
      case 'Q1': newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')]; break;
      case 'all': newRange = [null, null]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  const handleExportRawAges = async () => {
    setAgeExportLoading(true);
    try {
      const params = {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD')
      };
      if (eventTypeIds.length > 0) params.eventTypeIds = eventTypeIds.join(',');
      if (eventLocation) params.location = eventLocation;
      if (bookingFilter) params.bookingId = bookingFilter;
      if (excludeArchived) params.excludeArchived = 'true';
      if (cohortSex && cohortSex !== 'all') params.cohortSex = cohortSex;
      if (cohortRace && cohortRace !== 'all') params.cohortRace = cohortRace;
      if (cohortZips.length > 0) {
        params.cohortZips = cohortZips.join(',');
        // Back-compat for the single-zip age-export endpoint.
        if (cohortZips.length === 1) params.cohortZip = cohortZips[0];
      }
      if (cohortInsured && cohortInsured !== 'all') params.cohortInsured = cohortInsured;
      if (cohortStress && cohortStress !== 'all') params.cohortStress = cohortStress;
      if (cohortCounties.length > 0) params.cohortCounties = cohortCounties.join(',');
      if (cohortAgeMin != null) params.cohortAgeMin = String(cohortAgeMin);
      if (cohortAgeMax != null) params.cohortAgeMax = String(cohortAgeMax);
      const binsTrimmed = (ageExportBins || '').trim();
      if (binsTrimmed) params.bins = binsTrimmed;
      if (ageExportZipMode === 'zip3') params.zipMode = 'zip3';

      const { data: blob } = await api.getBlob('/screening/analytics/reports/age-export', params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `raw-ages-${dayjs().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Raw age export downloaded');
      setAgeExportOpen(false);
    } catch (error) {
      console.error('Error exporting raw ages:', error);
      message.error('Failed to export raw ages');
    } finally {
      setAgeExportLoading(false);
    }
  };

  const generateReport = async () => {
    if (selectedMetrics.length === 0) {
      message.warning('Please select at least one metric');
      return;
    }

    setLoading(true);
    try {
      // All time sends no dates (URLSearchParams would otherwise send "undefined").
      const params = new URLSearchParams();
      if (dateRange[0] && dateRange[1]) {
        params.set('startDate', dateRange[0].format('YYYY-MM-DD'));
        params.set('endDate', dateRange[1].format('YYYY-MM-DD'));
      }
      if (eventTypeIds.length > 0) params.set('eventTypeIds', eventTypeIds.join(','));
      if (eventLocation) params.set('location', eventLocation);
      if (bookingFilter) params.set('bookingId', bookingFilter);
      if (excludeArchived) params.set('excludeArchived', 'true');
      if (memberLimit) params.set('memberLimit', memberLimit.toString());
      if (cohortSex && cohortSex !== 'all') params.set('cohortSex', cohortSex);
      if (cohortRace && cohortRace !== 'all') params.set('cohortRace', cohortRace);
      if (cohortZips.length > 0) params.set('cohortZips', cohortZips.join(','));
      if (cohortInsured && cohortInsured !== 'all') params.set('cohortInsured', cohortInsured);
      if (cohortStress && cohortStress !== 'all') params.set('cohortStress', cohortStress);
      if (cohortCounties.length > 0) params.set('cohortCounties', cohortCounties.join(','));
      if (cohortAgeMin != null) params.set('cohortAgeMin', String(cohortAgeMin));
      if (cohortAgeMax != null) params.set('cohortAgeMax', String(cohortAgeMax));
      if (prioritizeData && selectedMetrics.length > 0) {
        params.set('prioritizeMetrics', selectedMetrics.join(','));
      }
      // Only send custom bands (keeps default reports on the server default).
      if (Array.isArray(ageBands) && !ageBandsEqual(ageBands, DEFAULT_AGE_BANDS)) {
        params.set('ageBands', JSON.stringify(ageBands));
      }

      const response = await api.post(`/screening/analytics/reports/custom?${params.toString()}`, {
        metrics: selectedMetrics
      });

      setReportData(response);
      setLastGenSnapshot({
        cohortSex,
        cohortRace,
        cohortZips: [...cohortZips],
        cohortInsured,
        cohortStress,
        cohortCounties: [...cohortCounties],
        cohortAgeMin,
        cohortAgeMax,
        memberLimit,
        prioritizeData,
        ageBands,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        eventTypeIds: [...eventTypeIds],
        eventLocation,
        bookingFilter,
        excludeArchived,
        metrics: [...selectedMetrics]
      });
      message.success('Report generated successfully');
      // Bring the fresh results into view (they render below the metric picker).
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  // Deep links from Pulse carry run=1: generate immediately so the user lands
  // on results. Runs once per mount; the URL-sync effect drops `run` from the
  // address bar, so a refresh won't silently re-run.
  const autoRunDone = useRef(false);
  useEffect(() => {
    if (autoRunDone.current) return;
    autoRunDone.current = true;
    if (URL_INIT.run === '1' && selectedMetrics.length > 0) generateReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCSV = () => {
    if (!reportData) return;

    const rows = [];
    // Label from the dates the report was GENERATED with (not the picker's
    // current, possibly-edited value) so the export always matches its numbers.
    const genRange = reportData?._appliedFilters ? reportData._appliedFilters.dateRange : undefined;
    const fmtDay = (d) => dayjs(d).format('MM/DD/YYYY');
    let dateLabel;
    if (genRange === null) dateLabel = 'All time';
    else if (genRange) dateLabel = `${fmtDay(genRange.startDate)} - ${fmtDay(genRange.endDate)}`;
    else dateLabel = dateRange[0] && dateRange[1]
      ? `${dateRange[0].format('MM/DD/YYYY')} - ${dateRange[1].format('MM/DD/YYYY')}`
      : 'All time';
    rows.push(['Custom Report', dateLabel]);
    if (memberLimit) rows.push(['Member Limit', `Latest ${memberLimit}`]);
    const cohortSizeForCsv = reportData?._sampling?.returned;
    if (cohortSizeForCsv != null) {
      rows.push(['Cohort Size', `${cohortSizeForCsv} members (each metric reports against the subset with that test — see per-metric Tested counts)`]);
    }
    // Provenance block: stamp the exact filters the report was generated from so
    // a downloaded CSV is self-describing (dates, filters, counts, denominators).
    const af = reportData?._appliedFilters;
    if (af) {
      rows.push(['Generated At', af.generatedAt || new Date().toISOString()]);
      const c = af.cohort || {};
      const filterBits = [];
      if (c.sex) filterBits.push(`Sex: ${c.sex}`);
      if (c.race) filterBits.push(`Race/Ethnicity: ${c.race}`);
      if (c.ageMin != null || c.ageMax != null) filterBits.push(`Age: ${c.ageMin != null ? c.ageMin : '0'}–${c.ageMax != null ? c.ageMax : '120'}`);
      if (Array.isArray(c.zips) && c.zips.length) filterBits.push(`ZIPs: ${c.zips.join('/')}`);
      if (Array.isArray(c.counties) && c.counties.length) filterBits.push(`County FIPS: ${c.counties.join('/')}`);
      if (c.insured) filterBits.push(`Insurance: ${c.insured}`);
      if (c.stress) filterBits.push(`Stress: ${c.stress}`);
      const scope = af.scope || {};
      if (scope.location) filterBits.push(`Location: ${scope.location}`);
      if (scope.bookingId) filterBits.push(`Booking: ${scope.bookingId}`);
      if (Array.isArray(scope.eventTypeIds) && scope.eventTypeIds.length) filterBits.push(`Event types: ${scope.eventTypeIds.length}`);
      rows.push(['Applied Filters', filterBits.length ? filterBits.join('; ') : 'None (all members in range)']);
      if (af.excludeArchived) rows.push(['Exclude Archived', 'Yes']);
    }
    rows.push([]);

    selectedMetrics.forEach(key => {
      const metricDef = ALL_METRICS.find(m => m.key === key);
      if (!metricDef) return;

      if (metricDef.isBreakdown && Array.isArray(reportData[key])) {
        const isLpaCsv = ['lpaStratification', 'lpaByAge', 'lpaBySex', 'lpaByRace', 'lpaByLocation', 'lpaByEvent'].includes(key);
        rows.push([metricDef.label]);
        const items = reportData[key];
        if (items.length > 0) {
          const testedTotal = items.reduce((sum, i) => sum + (typeof i.count === 'number' ? i.count : 0), 0);
          if (cohortSizeForCsv != null && testedTotal > 0 && testedTotal < cohortSizeForCsv) {
            rows.push([`(${testedTotal} of ${cohortSizeForCsv} cohort members tested — ${Math.round((testedTotal / cohortSizeForCsv) * 100)}%)`]);
          }
          const hasStrata = items[0].strata && Array.isArray(items[0].strata);
          const labelField = items[0].gender ? 'gender' : items[0].range ? 'range' : items[0].level ? 'level' : items[0].bucket ? 'bucket' : items[0].zip ? 'zip' : items[0].county ? 'county' : items[0].race ? 'race' : items[0].location ? 'location' : items[0].event ? 'event' : 'ethnicity';
          if (hasStrata) {
            const strataLabels = items[0].strata.map(s => isLpaCsv ? convertLpaRangeLabel(s.range) : s.range);
            rows.push([labelField.charAt(0).toUpperCase() + labelField.slice(1), 'Total', ...strataLabels]);
            items.forEach(item => {
              const rowLabel = isLpaCsv && labelField === 'range' ? convertLpaRangeLabel(item[labelField]) : item[labelField];
              // Groups with no valid results are reported explicitly, never as zeros folded into "normal".
              if (item.count === 0 || item.noResults) {
                rows.push([rowLabel, 'N/A — no results', ...item.strata.map(() => '')]);
              } else {
                rows.push([rowLabel, item.count, ...item.strata.map(s => s.count)]);
              }
            });
          } else if (labelField === 'bucket' && items.some(i => i.reportedCount != null || i.imputedCount != null)) {
            // Income: keep self-reported separate from statistically-modeled (imputed).
            rows.push(['Bucket', 'Total', 'Reported', 'Modeled (imputed)', 'Percentage']);
            items.forEach(item => {
              rows.push([item.bucket, item.count, item.reportedCount ?? '', item.imputedCount ?? '', `${item.percentage}%`]);
            });
          } else {
            const zipWithCounty = labelField === 'zip' && items.some(i => i.county);
            if (zipWithCounty) {
              rows.push(['Zip', 'County', 'Count', 'Percentage']);
              items.forEach(item => {
                rows.push([item.zip, item.county || '', item.count, `${item.percentage}%`]);
              });
            } else {
              rows.push([labelField.charAt(0).toUpperCase() + labelField.slice(1), 'Count', 'Percentage']);
              items.forEach(item => {
                const cell = isLpaCsv && labelField === 'range' ? convertLpaRangeLabel(item[labelField]) : item[labelField];
                rows.push([cell, item.count, `${item.percentage}%`]);
              });
            }
          }
        }
        rows.push([]);
      } else {
        let value = reportData[key];
        let suffix = metricDef.suffix || '';
        let csvLabel = metricDef.label;
        if (key === 'avgLPA') {
          value = convertLpa(value);
          suffix = ` ${lpaUnitLabel}`;
        }
        if (lpaUnit === 'mgdl') {
          if (key === 'lpaAbove75') csvLabel = 'Lp(a) ≥30 mg/dL';
          if (key === 'lpaAbove125') csvLabel = 'Lp(a) ≥50 mg/dL';
        }
        const displayValue = suffix ? `${value}${suffix}` : value;
        rows.push([csvLabel, displayValue]);
      }
    });

    const csvContent = rows.map(row => row.map(cell => `"${cell ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `custom-report-${dayjs().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    message.success('Report exported');
  };

  const sanitizeNarrativeText = (text = '') => {
    return text
      // Remove markdown heading prefixes like ###, ##, #
      .replace(/^\s{0,3}#{1,6}\s*/gm, '')
      // Remove markdown emphasis markers that sometimes leak through
      .replace(/\*\*(.*?)\*\*/g, '$1')
      // Normalize required section labels to plain text with colon
      .replace(/^\s*Community Need\s*:?\s*$/gim, 'Community Need:')
      .replace(/^\s*Key Findings\s*:?\s*$/gim, 'Key Findings:')
      .replace(/^\s*Equity Gaps\s*:?\s*$/gim, 'Equity Gaps:')
      .replace(/^\s*Funding Case\s*:?\s*$/gim, 'Funding Case:')
      // Collapse excessive blank lines
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const renderNarrativeFormatted = (text = '') => {
    const lines = text.split('\n');
    return lines.map((rawLine, idx) => {
      const line = rawLine.trim();

      if (!line) {
        return <div key={`space-${idx}`} style={{ height: 8 }} />;
      }

      if (/^(Community Need|Key Findings|Equity Gaps|Funding Case):$/i.test(line)) {
        return <NarrativeSectionTitle key={`section-${idx}`}>{line}</NarrativeSectionTitle>;
      }

      if (line.startsWith('- ')) {
        return <NarrativeBulletLine key={`bullet-${idx}`}>{line}</NarrativeBulletLine>;
      }

      return <NarrativeParagraphLine key={`line-${idx}`}>{line}</NarrativeParagraphLine>;
    });
  };

  const generateNarrative = async () => {
    if (!reportData) return;
    setNarrativeText('');
    setNarrativeLoading(true);

    try {
      const { API_BASE_URL } = await import('../../../constants/api');
      const response = await fetch(`${API_BASE_URL}/screening/analytics/reports/narrative`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportData,
          style: narrativeStyle,
          dateRange: {
            start: dateRange[0]?.format('YYYY-MM-DD'),
            end: dateRange[1]?.format('YYYY-MM-DD')
          },
          filters: {
            eventType: eventTypeIds.length > 0
              ? eventTypes.filter(e => eventTypeIds.includes(e._id)).map(e => e.name).join(', ')
              : undefined,
            location: eventLocation ? locationOptionLabel(eventLocation) : undefined,
            booking: bookings.find(b => b._id === bookingFilter)?.name,
            memberLimit
          }
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) { streamDone = true; break; }

        const text = decoder.decode(value, { stream: true });
        const lines = text.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const parsed = JSON.parse(line.slice(6));
              if (parsed.content) {
                accumulated += parsed.content;
                setNarrativeText(sanitizeNarrativeText(accumulated));
              }
              if (parsed.error) {
                message.error('Narrative generation failed');
              }
            } catch { /* skip malformed lines */ }
          }
        }
      }
    } catch (error) {
      console.error('Error generating narrative:', error);
      message.error('Failed to generate narrative');
    } finally {
      setNarrativeLoading(false);
    }
  };

  const copyNarrative = () => {
    navigator.clipboard.writeText(sanitizeNarrativeText(narrativeText));
    message.success('Copied to clipboard');
  };

  // Stale-report detection, computed at component level so both the in-results
  // banner AND the floating "re-generate" pill can use it. Compares current
  // filter inputs against the snapshot taken at last generation.
  const snapArraysEqual = (a, b) =>
    a.length === b.length && a.every((x, i) => x === b[i]);
  const filtersDirty = Boolean(lastGenSnapshot) && (() => {
    const snap = lastGenSnapshot;
    return (
      snap.cohortSex !== cohortSex ||
      snap.cohortRace !== cohortRace ||
      !snapArraysEqual(snap.cohortZips || [], cohortZips) ||
      (snap.cohortInsured || 'all') !== cohortInsured ||
      (snap.cohortStress || 'all') !== cohortStress ||
      !snapArraysEqual(snap.cohortCounties || [], cohortCounties) ||
      (snap.cohortAgeMin ?? null) !== cohortAgeMin ||
      (snap.cohortAgeMax ?? null) !== cohortAgeMax ||
      snap.memberLimit !== memberLimit ||
      snap.prioritizeData !== prioritizeData ||
      !ageBandsEqual(snap.ageBands || DEFAULT_AGE_BANDS, ageBands) ||
      snap.startDate !== dateRange[0]?.format('YYYY-MM-DD') ||
      snap.endDate !== dateRange[1]?.format('YYYY-MM-DD') ||
      !snapArraysEqual(snap.eventTypeIds || [], eventTypeIds) ||
      (snap.eventLocation || null) !== eventLocation ||
      snap.bookingFilter !== bookingFilter ||
      snap.excludeArchived !== excludeArchived ||
      !snapArraysEqual(snap.metrics || [], selectedMetrics)
    );
  })();

  const renderResults = () => {
    if (!reportData) return null;

    const statMetrics = selectedMetrics.filter(key => {
      const def = ALL_METRICS.find(m => m.key === key);
      return def && !def.isBreakdown;
    });

    const breakdownMetrics = selectedMetrics.filter(key => {
      const def = ALL_METRICS.find(m => m.key === key);
      return def && def.isBreakdown;
    });

    const hasLpaMetrics = selectedMetrics.some(key => LPA_KEYS.includes(key));

    // Cohort summary. The summary always reflects the settings that were used
    // to generate the CURRENT report (from the snapshot), not the user's
    // in-progress edits. The stale banner uses the component-level filtersDirty.
    const snap = lastGenSnapshot;
    const cohortDirty = filtersDirty;

    const cohortChips = [];
    if (snap) {
      const memberCount = reportData?._members?.length;
      const limitDescr = snap.memberLimit
        ? `Latest ${memberCount ?? snap.memberLimit} members`
        : `${memberCount ?? 'All'} members`;
      cohortChips.push(limitDescr);
      if (snap.cohortSex && snap.cohortSex !== 'all') cohortChips.push(snap.cohortSex);
      if (snap.cohortRace && snap.cohortRace !== 'all') cohortChips.push(raceLabel(snap.cohortRace));
      if (snap.cohortInsured && snap.cohortInsured !== 'all') cohortChips.push(snap.cohortInsured === 'insured' ? 'Insured' : 'Uninsured');
      if (snap.cohortStress && snap.cohortStress !== 'all') cohortChips.push(`${snap.cohortStress.charAt(0).toUpperCase()}${snap.cohortStress.slice(1)} stress`);
      if (Array.isArray(snap.cohortCounties) && snap.cohortCounties.length > 0) {
        cohortChips.push(snap.cohortCounties.length === 1
          ? (counties.find(c => c.fips === snap.cohortCounties[0])?.label || 'County')
          : `${snap.cohortCounties.length} counties`);
      }
      if (Array.isArray(snap.cohortZips) && snap.cohortZips.length > 0) {
        const zipLabel = (z) => z.length === 3 ? `${z}xx` : z;
        cohortChips.push(snap.cohortZips.length === 1
          ? `Zip ${zipLabel(snap.cohortZips[0])}`
          : `${snap.cohortZips.length} ZIPs`);
      }
      if (snap.cohortAgeMin != null || snap.cohortAgeMax != null) {
        cohortChips.push(`Ages ${snap.cohortAgeMin != null ? snap.cohortAgeMin : '0'}–${snap.cohortAgeMax != null ? snap.cohortAgeMax : '120'}`);
      }
      if (Array.isArray(snap.eventTypeIds) && snap.eventTypeIds.length > 0) {
        cohortChips.push(snap.eventTypeIds.length === 1
          ? (eventTypes.find(e => e._id === snap.eventTypeIds[0])?.name || '1 event type')
          : `${snap.eventTypeIds.length} event types`);
      }
      if (snap.eventLocation) cohortChips.push(locationOptionLabel(snap.eventLocation));
      if (snap.startDate && snap.endDate) {
        cohortChips.push(`${dayjs(snap.startDate).format('MMM D, YYYY')} – ${dayjs(snap.endDate).format('MMM D, YYYY')}`);
      }
    }

    return (
      <ReportCard
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>
              Report Results
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {hasLpaMetrics && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666' }}>
                  Lp(a):
                  <Switch
                    size="small"
                    checked={lpaUnit === 'mgdl'}
                    onChange={(checked) => setLpaUnit(checked ? 'mgdl' : 'nmol')}
                    checkedChildren="mg/dL"
                    unCheckedChildren="nmol/L"
                  />
                </span>
              )}
              <Button icon={<DownloadOutlined />} onClick={handleExportCSV} size="small">
                Export CSV
              </Button>
            </div>
          </div>
        }
      >
        {cohortChips.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            padding: '8px 12px',
            marginBottom: 12,
            background: '#f5f7fa',
            borderLeft: `3px solid ${COLORS.primaryBlue || '#1677ff'}`,
            borderRadius: 4,
            fontSize: 12
          }}>
            <span style={{ fontWeight: 600, color: COLORS.textMedium, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4 }}>
              Cohort:
            </span>
            {cohortChips.map((c, i) => (
              <Tag key={i} style={{ margin: 0, fontSize: 11 }}>{c}</Tag>
            ))}
          </div>
        )}
        {cohortDirty && (
          <SamplingBanner $variant="warn">
            <InfoCircleOutlined style={{ color: '#fa8c16', marginTop: 2 }} />
            <span>
              <b>Cohort settings changed.</b> The report below still reflects the previous cohort.
              Click <b>Generate Report</b> to apply your changes.
            </span>
          </SamplingBanner>
        )}
        {reportData?._sampling && (() => {
          const s = reportData._sampling;
          const fmt = (iso) => iso ? dayjs(iso).format('MMM D, YYYY') : null;
          if (s.exhausted) {
            return (
              <SamplingBanner $variant="warn">
                <InfoCircleOutlined style={{ color: '#fa8c16', marginTop: 2 }} />
                <span>
                  Only <b>{s.returned}</b> of <b>{s.requested}</b> requested members exist across the entire dataset for the selected Date / Scope / Cohort filters. Showing what we found. Each metric below reports against the subset of these members that has that test (see each metric&apos;s <b>Tested</b> count).
                </span>
              </SamplingBanner>
            );
          }
          if (s.expanded) {
            return (
              <SamplingBanner>
                <InfoCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
                <span>
                  Expanded date range to find <b>{s.returned}</b> cohort members
                  {s.actualDateRange && (
                    <> ({fmt(s.actualDateRange.start) || 'all time'} – {fmt(s.actualDateRange.end) || 'today'})</>
                  )}. Each metric reports against the subset of these members that has that test (see each metric&apos;s <b>Tested</b> count).
                </span>
              </SamplingBanner>
            );
          }
          if (s.requested && s.returned === s.requested) {
            return (
              <SamplingBanner>
                <InfoCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
                <span>
                  <b>Cohort: {s.returned} members</b> matching Date / Scope / Cohort filters. Each metric reports against the subset of the cohort that has that test (see each metric&apos;s <b>Tested</b> count for its denominator).
                </span>
              </SamplingBanner>
            );
          }
          if (s.returned > 0) {
            return (
              <SamplingBanner>
                <InfoCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
                <span>
                  <b>Cohort: {s.returned} members</b> matching Date / Scope / Cohort filters. Each metric reports against the subset that has that test (see each metric&apos;s <b>Tested</b> count).
                </span>
              </SamplingBanner>
            );
          }
          return null;
        })()}
        {reportData?._sampling?.prioritization && (() => {
          const pri = reportData._sampling.prioritization;
          const labels = (pri.metrics || [])
            .map(k => ALL_METRICS.find(m => m.key === k)?.label || k);
          if (!labels.length) return null;
          const total = (pri.selectedWithData || 0) + (pri.filler || 0);
          return (
            <SamplingBanner>
              <InfoCircleOutlined style={{ color: '#1677ff', marginTop: 2 }} />
              <span>
                <b>Prioritized cohort:</b> {total} members &mdash; <b>{pri.selectedWithData || 0}</b> with data for {labels.join(', ')}
                {pri.totalWithData > (pri.selectedWithData || 0) && (
                  <> (of {pri.totalWithData} total in the date range)</>
                )}
                {pri.filler > 0 && <> + <b>{pri.filler}</b> most-recent filler{pri.filler === 1 ? '' : 's'}</>}.
                Toggle this off in the cohort filters above to revert to a recency-only cohort.
              </span>
            </SamplingBanner>
          );
        })()}

        {statMetrics.length > 0 && (
          <Row gutter={[20, 20]} style={{ marginBottom: breakdownMetrics.length > 0 ? 28 : 0 }}>
            {statMetrics.map(key => {
              const def = ALL_METRICS.find(m => m.key === key);
              let value = reportData[key];
              let suffix = def.suffix || '';
              let label = def.label;
              // Convert Lp(a) values/labels if user toggled to mg/dL
              if (key === 'avgLPA') {
                value = convertLpa(value);
                suffix = ` ${lpaUnitLabel}`;
              }
              if (lpaUnit === 'mgdl') {
                if (key === 'lpaAbove75') label = 'Lp(a) ≥30 mg/dL';
                if (key === 'lpaAbove125') label = 'Lp(a) ≥50 mg/dL';
              }
              return (
                <Col xs={12} sm={8} md={6} key={key}>
                  <ResultStatCard>
                    <Statistic
                      title={label}
                      value={value ?? '-'}
                      suffix={suffix}
                      valueStyle={{ color: COLORS.textDark }}
                    />
                  </ResultStatCard>
                </Col>
              );
            })}
          </Row>
        )}

        {breakdownMetrics.map(key => {
          const def = ALL_METRICS.find(m => m.key === key);
          const items = reportData[key];
          if (!Array.isArray(items) || items.length === 0) return null;

          const isLpaBreakdown = ['lpaStratification', 'lpaByAge', 'lpaBySex', 'lpaByRace', 'lpaByLocation', 'lpaByEvent'].includes(key);
          const hasStrata = items[0].strata && Array.isArray(items[0].strata);
          const labelField = items[0].gender ? 'gender' : items[0].range ? 'range' : items[0].level ? 'level' : items[0].bucket ? 'bucket' : items[0].zip ? 'zip' : items[0].county ? 'county' : items[0].race ? 'race' : items[0].location ? 'location' : items[0].event ? 'event' : 'ethnicity';

          // Per-metric denominator annotation: "N tested of M cohort"
          const cohortSize = reportData?._sampling?.returned;
          const testedTotal = items.reduce((sum, i) => sum + (typeof i.count === 'number' ? i.count : 0), 0);
          const showCoverage = cohortSize != null && testedTotal > 0 && testedTotal < cohortSize;
          const coverageNote = showCoverage
            ? `${testedTotal} of ${cohortSize} cohort members have this test (${Math.round((testedTotal / cohortSize) * 100)}%)`
            : null;

          if (hasStrata) {
            const strataLabels = items[0].strata.map(s => isLpaBreakdown ? convertLpaRangeLabel(s.range) : s.range);
            return (
              <div key={key} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: coverageNote ? 2 : 10, color: COLORS.textDark }}>
                  {def.label}
                </div>
                {coverageNote && (
                  <div style={{ fontSize: 11, color: COLORS.textMedium, marginBottom: 8, fontStyle: 'italic' }}>
                    {coverageNote}
                  </div>
                )}
                <BreakdownTable>
                  <BreakdownRow style={{ background: '#fafafa', fontWeight: 600, fontSize: 12, color: '#666' }}>
                    <span style={{ flex: 1, minWidth: 80 }}>{labelField.charAt(0).toUpperCase() + labelField.slice(1)}</span>
                    <span style={{ width: 55, textAlign: 'right' }}>Total</span>
                    {strataLabels.map(s => (
                      <span key={s} style={{ width: 100, textAlign: 'right' }}>{s}</span>
                    ))}
                  </BreakdownRow>
                  {items.map((item, idx) => (
                    <BreakdownRow key={idx}>
                      <span style={{ flex: 1, minWidth: 80, fontWeight: 500 }}>{item[labelField]}</span>
                      {(item.count === 0 || item.noResults) ? (
                        <span style={{ flex: 'none', textAlign: 'right', color: '#999', fontStyle: 'italic', fontSize: 12, paddingRight: 4 }}>
                          N/A — no results
                        </span>
                      ) : (
                        <>
                          <span style={{ width: 55, textAlign: 'right', fontWeight: 600 }}>{item.count}</span>
                          {item.strata.map((s, si) => (
                            <span key={si} style={{ width: 100, textAlign: 'right' }}>
                              {s.count > 0 ? <>{s.count} <span style={{ color: '#888', fontSize: 11 }}>({s.percentage}%)</span></> : '-'}
                            </span>
                          ))}
                        </>
                      )}
                    </BreakdownRow>
                  ))}
                </BreakdownTable>
              </div>
            );
          }

          const maxCount = Math.max(...items.map(i => i.count));

          return (
            <div key={key} style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: coverageNote ? 2 : 10, color: COLORS.textDark }}>
                {def.label}
              </div>
              {coverageNote && (
                <div style={{ fontSize: 11, color: COLORS.textMedium, marginBottom: 8, fontStyle: 'italic' }}>
                  {coverageNote}
                </div>
              )}
              <BreakdownTable>
                <BreakdownRow style={{ background: '#fafafa', fontWeight: 600, fontSize: 12, color: '#666' }}>
                  <span style={{ flex: 1 }}>{labelField.charAt(0).toUpperCase() + labelField.slice(1)}</span>
                  <span style={{ width: 200, textAlign: 'center' }}>Distribution</span>
                  <span style={{ width: 60, textAlign: 'right' }}>Count</span>
                  <span style={{ width: 60, textAlign: 'right' }}>%</span>
                </BreakdownRow>
                {items.map((item, idx) => (
                  <BreakdownRow key={idx}>
                    <span style={{ flex: 1, fontWeight: 500 }}>
                      {isLpaBreakdown && labelField === 'range' ? convertLpaRangeLabel(item[labelField]) : item[labelField]}
                      {labelField === 'zip' && item.county && (
                        <span style={{ color: '#888', fontWeight: 400, marginLeft: 6, fontSize: 12 }}>{item.county}</span>
                      )}
                    </span>
                    <span style={{ width: 200 }}>
                      <BarFill $width={maxCount > 0 ? (item.count / maxCount) * 100 : 0} />
                    </span>
                    <span style={{ width: 60, textAlign: 'right' }}>{item.count}</span>
                    <span style={{ width: 60, textAlign: 'right', color: '#888' }}>{item.percentage}%</span>
                  </BreakdownRow>
                ))}
              </BreakdownTable>
            </div>
          );
        })}

        {Array.isArray(reportData?._members) && reportData._members.length > 0 && (() => {
          // Determine which value columns to display based on selected metrics
          const COL_MAP = [
            { key: 'systolicBP', label: 'SBP', prefix: 'bp', metrics: ['avgSystolic', 'avgDiastolic', 'highBPCount', 'highBPPercent'] },
            { key: 'lipoproteinA', label: `Lp(a) ${lpaUnitLabel}`, prefix: 'lpa', metrics: LPA_KEYS },
            { key: 'ldlCholesterol', label: 'LDL', prefix: 'ldl', metrics: ['avgLDL'] },
            { key: 'hdlCholesterol', label: 'HDL', prefix: 'hdl', metrics: ['avgHDL'] },
            { key: 'totalCholesterol', label: 'TC', prefix: 'chol', metrics: ['avgCholesterol', 'highCholesterolCount'] },
            { key: 'triglycerides', label: 'Trig', prefix: 'trig', metrics: ['avgTriglycerides'] },
            { key: 'a1c', label: 'A1C', prefix: 'a1c', metrics: ['avgA1C'] },
            { key: 'bloodGlucose', label: 'Glu', metrics: ['avgGlucose'] },
            { key: 'eGFR', label: 'eGFR', prefix: 'egfr', metrics: ['avgEGFR'] },
            { key: 'psa', label: 'PSA', prefix: 'psa', metrics: [] },
            { key: 'bmi', label: 'BMI', metrics: ['avgBMI'] },
            { key: 'whr', label: 'WHtR', prefix: 'whr', metrics: [] },
            { key: 'visceralFat', label: 'Visceral Fat', prefix: 'visceral', metrics: [] }
          ];
          // `prefix` catches every metric of a measure (incl. by-race/location/
          // event cross-tabs) so new breakdowns never hide the value column.
          const visibleCols = COL_MAP.filter(c => selectedMetrics.some(m => c.metrics.includes(m) || (c.prefix && m.startsWith(c.prefix))));
          if (visibleCols.length === 0) visibleCols.push(COL_MAP[1]); // fallback to Lp(a)

          // Master spreadsheet column list for the customizable members table.
          // Each entry: { key, label, group, accessor?, format? }.
          //   accessor(m) returns the raw cell value (defaults to m.values[key])
          //   format(val, m) returns the display string (defaults to fmtVal(key, val))
          // The user can pick any subset via the "Columns" dropdown; the choice
          // is persisted in localStorage. When no custom selection exists, the
          // table defaults to the metric-derived `visibleCols` above.
          const ALL_SPREADSHEET_COLS = [
            // Metric columns (reuse COL_MAP labels so headers match per-metric charts)
            ...COL_MAP.map(c => ({ ...c, group: 'Metrics' })),
            // Vitals
            { key: 'diastolicBP', label: 'DBP', group: 'Vitals' },
            { key: 'heartRate', label: 'HR', group: 'Vitals' },
            { key: 'temperature', label: 'Temp', group: 'Vitals' },
            { key: 'respiratoryRate', label: 'RR', group: 'Vitals' },
            { key: 'oxygenSaturation', label: 'SpO₂', group: 'Vitals' },
            // Body measurements
            { key: 'heightInches', label: 'Height (in)', group: 'Body' },
            { key: 'weightLbs', label: 'Weight (lb)', group: 'Body' },
            { key: 'waistInches', label: 'Waist (in)', group: 'Body' },
            { key: 'hipInches', label: 'Hip (in)', group: 'Body' },
            { key: 'subcutaneousFat', label: 'Subcut Fat', group: 'Body' },
            // Other blood markers
            { key: 'creatinine', label: 'Creatinine', group: 'Blood' },
            { key: 'nonHdlCholesterol', label: 'Non-HDL', group: 'Blood' },
            {
              key: 'apolipoproteinB',
              label: 'ApoB',
              group: 'Blood',
              // Prefer measured ApoB; otherwise fall back to the Non-HDL estimate
              // (Total − HDL) and mark it as an estimate.
              accessor: m => (m.values?.apolipoproteinB != null
                ? m.values.apolipoproteinB
                : m.values?.nonHdlCholesterol),
              format: (v, m) => {
                if (v == null || v === '') return '–';
                const n = Math.round(parseFloat(v));
                if (isNaN(n)) return '–';
                const estimated = m.values?.apolipoproteinB == null && m.values?.nonHdlCholesterol != null;
                return estimated ? `${n} est` : `${n}`;
              }
            },
            { key: 'cReactiveProtein', label: 'CRP', group: 'Blood' },
            // Identity / contact (top-level on member)
            { key: 'firstName', label: 'First Name', group: 'Identity', accessor: m => m.firstName, format: v => v || '–' },
            { key: 'lastName', label: 'Last Name', group: 'Identity', accessor: m => m.lastName, format: v => v || '–' },
            { key: 'dateOfBirth', label: 'DOB', group: 'Identity', accessor: m => m.dateOfBirth, format: v => v ? dayjs(v).format('MM/DD/YYYY') : '–' },
            { key: 'raceEthnicity', label: 'Race/Ethnicity', group: 'Identity', accessor: m => m.raceEthnicity, format: v => v || '–' },
            { key: 'insured', label: 'Insured', group: 'Identity', accessor: m => m.insured, format: v => v || '–' },
            { key: 'stressCategory', label: 'Stress', group: 'Identity', accessor: m => m.stressLevel, format: (v, m) => (v != null && v !== '') ? `${v} (${m.stressCategory})` : '–' },
            { key: 'county', label: 'County', group: 'Contact', accessor: m => m.county, format: v => v || '–' },
            { key: 'email', label: 'Email', group: 'Contact', accessor: m => m.email, format: v => v || '–' },
            { key: 'phone', label: 'Phone', group: 'Contact', accessor: m => m.phone, format: v => v || '–' },
            { key: 'zip', label: 'ZIP', group: 'Contact', accessor: m => m.zip, format: v => v || '–' },
            // Visit metadata
            { key: 'visitDate', label: 'Visit Date', group: 'Visit', accessor: m => m.visitDate, format: v => v ? dayjs(v).format('MM/DD/YYYY') : '–' },
            { key: 'eventName', label: 'Event', group: 'Visit', accessor: m => m.eventName, format: v => v || '–' },
            { key: 'visitId', label: 'Visit ID', group: 'Visit', accessor: m => m.visitId, format: v => v || '–' }
          ];
          // De-dupe by key (COL_MAP entries take precedence; later duplicates ignored)
          const ALL_COLS = (() => {
            const seen = new Set();
            const out = [];
            for (const c of ALL_SPREADSHEET_COLS) {
              if (seen.has(c.key)) continue;
              seen.add(c.key);
              out.push(c);
            }
            return out;
          })();
          // If the user has chosen specific columns, those override the auto set.
          const customCols = (Array.isArray(spreadsheetCols) && spreadsheetCols.length > 0)
            ? spreadsheetCols
                .map(k => ALL_COLS.find(c => c.key === k))
                .filter(Boolean)
            : null;
          const effectiveCols = customCols || visibleCols;
          const effectiveColKeys = new Set(effectiveCols.map(c => c.key));
          const colAccessor = (col, m) => (col.accessor ? col.accessor(m) : m.values?.[col.key]);
          const colFormat = (col, m) => {
            const v = colAccessor(col, m);
            if (col.format) return col.format(v, m);
            return fmtVal(col.key, v);
          };

          // Bucket definitions per metric column. `unit` is appended to labels.
          // For Lp(a) we adapt to whichever unit the user toggled.
          const lpaBuckets = lpaUnit === 'mgdl'
            ? [
                { key: 'lpa_lt30', label: '<30 mg/dL (Normal)', test: v => v != null && v / 2.5 < 30 },
                { key: 'lpa_30_49', label: '30–49 mg/dL (Intermediate)', test: v => v != null && v / 2.5 >= 30 && v / 2.5 < 50 },
                { key: 'lpa_gte50', label: '≥50 mg/dL (High Risk)', test: v => v != null && v / 2.5 >= 50 }
              ]
            : [
                { key: 'lpa_lt75', label: '<75 nmol/L (Normal)', test: v => v != null && v < 75 },
                { key: 'lpa_75_124', label: '75–124 nmol/L (Intermediate)', test: v => v != null && v >= 75 && v < 125 },
                { key: 'lpa_gte125', label: '≥125 nmol/L (High Risk)', test: v => v != null && v >= 125 }
              ];

          const COL_BUCKETS = {
            lipoproteinA: lpaBuckets,
            ldlCholesterol: [
              { key: 'ldl_lt100', label: '<100 mg/dL (Optimal)', test: v => v != null && v < 100 },
              { key: 'ldl_100_129', label: '100–129 mg/dL (Near Optimal)', test: v => v != null && v >= 100 && v < 130 },
              { key: 'ldl_130_159', label: '130–159 mg/dL (Borderline)', test: v => v != null && v >= 130 && v < 160 },
              { key: 'ldl_160_189', label: '160–189 mg/dL (High)', test: v => v != null && v >= 160 && v < 190 },
              { key: 'ldl_gte190', label: '≥190 mg/dL (Very High)', test: v => v != null && v >= 190 }
            ],
            hdlCholesterol: [
              { key: 'hdl_lt40', label: '<40 mg/dL (Low)', test: v => v != null && v < 40 },
              { key: 'hdl_40_59', label: '40–59 mg/dL (Normal)', test: v => v != null && v >= 40 && v < 60 },
              { key: 'hdl_gte60', label: '≥60 mg/dL (Protective)', test: v => v != null && v >= 60 }
            ],
            totalCholesterol: [
              { key: 'tc_lt200', label: '<200 mg/dL (Desirable)', test: v => v != null && v < 200 },
              { key: 'tc_200_239', label: '200–239 mg/dL (Borderline)', test: v => v != null && v >= 200 && v < 240 },
              { key: 'tc_gte240', label: '≥240 mg/dL (High)', test: v => v != null && v >= 240 }
            ],
            triglycerides: [
              { key: 'trig_lt150', label: '<150 mg/dL (Normal)', test: v => v != null && v < 150 },
              { key: 'trig_150_199', label: '150–199 mg/dL (Borderline)', test: v => v != null && v >= 150 && v < 200 },
              { key: 'trig_200_499', label: '200–499 mg/dL (High)', test: v => v != null && v >= 200 && v < 500 },
              { key: 'trig_gte500', label: '≥500 mg/dL (Very High)', test: v => v != null && v >= 500 }
            ],
            a1c: [
              { key: 'a1c_lt57', label: '<5.7% (Normal)', test: v => v != null && v < 5.7 },
              { key: 'a1c_57_64', label: '5.7–6.4% (Prediabetic)', test: v => v != null && v >= 5.7 && v < 6.5 },
              { key: 'a1c_65_89', label: '6.5–8.9% (Diabetic)', test: v => v != null && v >= 6.5 && v < 9 },
              { key: 'a1c_gte9', label: '≥9% (Uncontrolled)', test: v => v != null && v >= 9 }
            ],
            bloodGlucose: [
              { key: 'glu_lt100', label: '<100 mg/dL (Normal)', test: v => v != null && v < 100 },
              { key: 'glu_100_125', label: '100–125 mg/dL (Prediabetic)', test: v => v != null && v >= 100 && v < 126 },
              { key: 'glu_gte126', label: '≥126 mg/dL (Diabetic)', test: v => v != null && v >= 126 }
            ],
            eGFR: [
              { key: 'egfr_gte90', label: '≥90 (Normal)', test: v => v != null && v >= 90 },
              { key: 'egfr_60_89', label: '60–89 (Mild reduction)', test: v => v != null && v >= 60 && v < 90 },
              { key: 'egfr_45_59', label: '45–59 (Stage 3a)', test: v => v != null && v >= 45 && v < 60 },
              { key: 'egfr_30_44', label: '30–44 (Stage 3b)', test: v => v != null && v >= 30 && v < 45 },
              { key: 'egfr_15_29', label: '15–29 (Stage 4)', test: v => v != null && v >= 15 && v < 30 },
              { key: 'egfr_lt15', label: '<15 (Stage 5 / kidney failure)', test: v => v != null && v < 15 }
            ],
            systolicBP: [
              { key: 'bp_lt120', label: '<120 (Normal)', test: v => v != null && v < 120 },
              { key: 'bp_120_129', label: '120–129 (Elevated)', test: v => v != null && v >= 120 && v < 130 },
              { key: 'bp_130_139', label: '130–139 (Stage 1)', test: v => v != null && v >= 130 && v < 140 },
              { key: 'bp_140_179', label: '140–179 (Stage 2)', test: v => v != null && v >= 140 && v < 180 },
              { key: 'bp_gte180', label: '≥180 (Crisis)', test: v => v != null && v >= 180 }
            ],
            psa: [
              { key: 'psa_lt4', label: '<4 ng/mL (Normal)', test: v => v != null && v < 4 },
              { key: 'psa_4_99', label: '4–9.9 ng/mL (Intermediate)', test: v => v != null && v >= 4 && v < 10 },
              { key: 'psa_gte10', label: '≥10 ng/mL (High Risk)', test: v => v != null && v >= 10 }
            ],
            bmi: [
              { key: 'bmi_lt25', label: '<25 (Normal)', test: v => v != null && v < 25 },
              { key: 'bmi_25_299', label: '25–29.9 (Overweight)', test: v => v != null && v >= 25 && v < 30 },
              { key: 'bmi_30_399', label: '30–39.9 (Obese)', test: v => v != null && v >= 30 && v < 40 },
              { key: 'bmi_gte40', label: '≥40 (Severe Obesity)', test: v => v != null && v >= 40 }
            ],
            // WHtR uses a single non-sex-specific cutpoint set.
            whr: [
              { key: 'whr_normal', label: 'Normal (<0.50)', test: v => v != null && v < 0.50 },
              { key: 'whr_moderate', label: 'Moderate (0.50–0.59)', test: v => v != null && v >= 0.50 && v < 0.60 },
              { key: 'whr_high', label: 'High (≥0.60)', test: v => v != null && v >= 0.60 }
            ]
          };

          // Build grouped filter options based on visible columns
          const filterGroups = visibleCols
            .filter(c => COL_BUCKETS[c.key])
            .map(c => ({
              label: c.label,
              key: c.key,
              options: COL_BUCKETS[c.key].map(b => ({
                value: `${c.key}::${b.key}`,
                label: b.label
              }))
            }));

          // Filter (combined: risk/bucket AND sex AND age range)
          let rows = reportData._members.filter(m => {
            // Sex filter
            if (sexFilter !== 'all' && (m.sex || 'Unknown') !== sexFilter) return false;
            // Age range filter
            if (ageRange.min != null && (m.age == null || m.age < ageRange.min)) return false;
            if (ageRange.max != null && (m.age == null || m.age > ageRange.max)) return false;
            // Risk / bucket filter
            const sevs = (m.flags || []).map(f => f.severity);
            if (memberFilter === 'all') return true;
            if (memberFilter === 'highRisk') return sevs.includes('high');
            if (memberFilter === 'mediumPlus') return sevs.includes('high') || sevs.includes('medium');
            if (memberFilter === 'noRisk') return sevs.length === 0;
            // Bucket filter: format "<colKey>::<bucketKey>"
            if (memberFilter.includes('::')) {
              const [colKey, bucketKey] = memberFilter.split('::');
              const bucket = COL_BUCKETS[colKey]?.find(b => b.key === bucketKey);
              if (!bucket) return true;
              const raw = m.values?.[colKey];
              const v = raw == null || raw === '' ? null : parseFloat(raw);
              return bucket.test(v, m);
            }
            return true;
          });

          // Sort
          const riskScore = (m) => {
            const sevs = (m.flags || []).map(f => f.severity);
            return (sevs.filter(s => s === 'high').length * 10) + sevs.filter(s => s === 'medium').length;
          };
          rows = [...rows].sort((a, b) => {
            let av, bv;
            if (memberSort.field === 'risk') { av = riskScore(a); bv = riskScore(b); }
            else if (memberSort.field === 'name') { av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); }
            else if (memberSort.field === 'age') { av = a.age ?? -1; bv = b.age ?? -1; }
            else { // value column — supports both metric keys (m.values[k])
              // and accessor-based spreadsheet columns (top-level fields like
              // email, eventName, dateOfBirth). Numeric sort when both cells
              // parse as numbers; otherwise fall back to case-insensitive
              // string compare so sorting Name/Event/etc. behaves naturally.
              const col = effectiveCols.find(c => c.key === memberSort.field);
              const rawA = col ? colAccessor(col, a) : a.values?.[memberSort.field];
              const rawB = col ? colAccessor(col, b) : b.values?.[memberSort.field];
              const numA = parseFloat(rawA);
              const numB = parseFloat(rawB);
              const bothNumeric = !isNaN(numA) && !isNaN(numB);
              if (bothNumeric) {
                av = numA; bv = numB;
              } else {
                av = (rawA == null || rawA === '') ? '' : String(rawA).toLowerCase();
                bv = (rawB == null || rawB === '') ? '' : String(rawB).toLowerCase();
              }
            }
            const cmp = av < bv ? -1 : av > bv ? 1 : 0;
            return memberSort.dir === 'desc' ? -cmp : cmp;
          });

          const toggleSort = (field) => {
            setMemberSort(prev => prev.field === field
              ? { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
              : { field, dir: 'desc' });
          };
          const sortIndicator = (field) => memberSort.field === field
            ? (memberSort.dir === 'desc' ? ' ▼' : ' ▲')
            : '';

          const fmtVal = (key, val) => {
            if (val == null || val === '') return '–';
            const n = parseFloat(val);
            if (isNaN(n)) return val;
            if (key === 'lipoproteinA' && lpaUnit === 'mgdl') return Math.round(n / 2.5);
            if (key === 'whr') return n.toFixed(2);
            if (key === 'a1c') return n.toFixed(1);
            return Math.round(n);
          };

          // Direct HIPAA identifiers. The anonymized export drops these columns
          // and swaps the Name column for a coded Patient ID, so a shared/leaked
          // file cannot expose PHI on its own. The regular export keeps them.
          const PHI_EXPORT_KEYS = new Set(['firstName', 'lastName', 'dateOfBirth', 'email', 'phone']);
          const exportMembersCSV = (anonymized = false) => {
            const exportCols = anonymized
              ? effectiveCols.filter(c => !PHI_EXPORT_KEYS.has(c.key))
              : effectiveCols;
            const header = [anonymized ? 'Patient ID' : 'Name', 'Age', 'Sex', ...exportCols.map(c => c.label), 'Flags'];
            const lines = [header.join(',')];
            rows.forEach(m => {
              const flagText = (m.flags || []).map(f => f.label).join('; ');
              const idCell = anonymized
                ? String(m.patientId || m._id || '')
                : (m.name || '—');
              const row = [
                `"${idCell.replace(/"/g, '""')}"`,
                m.age ?? '',
                m.sex || '',
                ...exportCols.map(c => {
                  const cell = colFormat(c, m);
                  // Quote any non-numeric / contains-special-char cells for CSV safety.
                  const s = cell == null ? '' : String(cell);
                  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                }),
                `"${flagText.replace(/"/g, '""')}"`
              ];
              lines.push(row.join(','));
            });
            const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `members${anonymized ? '-deidentified' : ''}-${dayjs().format('YYYY-MM-DD')}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          };

          const total = reportData._members.length;
          const highCount = reportData._members.filter(m => (m.flags || []).some(f => f.severity === 'high')).length;
          const mediumCount = reportData._members.filter(m => (m.flags || []).some(f => f.severity === 'medium') && !(m.flags || []).some(f => f.severity === 'high')).length;
          const noRiskCount = reportData._members.filter(m => !(m.flags || []).length).length;

          return (
            <MembersSection>
              <MembersHeaderRow>
                <div
                  onClick={() => setMembersExpanded(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: COLORS.textDark }}
                >
                  <FontAwesomeIcon icon={membersExpanded ? faChevronDown : faChevronRight} />
                  Members in this report ({rows.length}{rows.length !== total && ` of ${total}`})
                  <span style={{ fontWeight: 400, fontSize: 12, color: '#888', marginLeft: 8 }}>
                    {highCount} high · {mediumCount} medium · {noRiskCount} no risk
                  </span>
                </div>
              </MembersHeaderRow>
              {membersExpanded && (
                <MembersToolbar>
                  <ToolbarField>
                    <Tooltip title="View filters narrow what's shown in this table only. They do not change the report's averages, charts, or AI narrative. To reshape the underlying cohort, use the Cohort filters in the builder above and re-Generate.">
                      <span className="field-label">
                        View <InfoCircleOutlined style={{ fontSize: 10 }} />
                      </span>
                    </Tooltip>
                    <div className="field-control">
                      <Select
                        value={memberFilter}
                        onChange={setMemberFilter}
                        style={{ minWidth: 250 }}
                        dropdownStyle={{ minWidth: 320 }}
                        options={[
                          {
                            label: 'All',
                            options: [{ value: 'all', label: 'All members' }]
                          },
                          {
                            label: 'Risk severity',
                            options: [
                              { value: 'highRisk', label: 'High risk only' },
                              { value: 'mediumPlus', label: 'Any risk flag (medium or high)' },
                              { value: 'noRisk', label: 'No risk flags' }
                            ]
                          },
                          ...filterGroups.map(g => ({
                            label: `${g.label} buckets`,
                            options: g.options
                          }))
                        ]}
                      />
                    </div>
                  </ToolbarField>

                  <ToolbarField>
                    <span className="field-label">Sex</span>
                    <div className="field-control">
                      <Select
                        value={sexFilter}
                        onChange={setSexFilter}
                        style={{ minWidth: 130 }}
                        options={[
                          { value: 'all', label: 'Any sex' },
                          { value: 'Male', label: 'Male' },
                          { value: 'Female', label: 'Female' },
                          { value: 'Other', label: 'Other' },
                          { value: 'Unknown', label: 'Unknown' }
                        ]}
                      />
                    </div>
                  </ToolbarField>

                  <ToolbarField>
                    <span className="field-label">Age range</span>
                    <div className="field-control">
                      <InputNumber
                        min={0}
                        max={120}
                        placeholder="min"
                        value={ageRange.min}
                        onChange={(v) => setAgeRange(r => ({ ...r, min: v }))}
                        style={{ width: 72 }}
                      />
                      <span style={{ color: '#999' }}>–</span>
                      <InputNumber
                        min={0}
                        max={120}
                        placeholder="max"
                        value={ageRange.max}
                        onChange={(v) => setAgeRange(r => ({ ...r, max: v }))}
                        style={{ width: 72 }}
                      />
                      {(ageRange.min != null || ageRange.max != null) && (
                        <Button
                          type="link"
                          style={{ padding: '0 4px', height: 'auto', fontSize: 12 }}
                          onClick={() => setAgeRange({ min: null, max: null })}
                        >
                          clear
                        </Button>
                      )}
                    </div>
                  </ToolbarField>

                  <ToolbarField>
                    <span className="field-label">Identify</span>
                    <div className="field-control">
                      <Switch
                        checked={identifyMembers}
                        onChange={setIdentifyMembers}
                        checkedChildren="On"
                        unCheckedChildren="Off"
                      />
                    </div>
                  </ToolbarField>

                  <ToolbarField className="actions">
                    <Dropdown
                      trigger={['click']}
                      placement="bottomRight"
                      dropdownRender={() => {
                        // Group columns by their `group` prop for a cleaner picker.
                        const groups = ALL_COLS.reduce((acc, c) => {
                          const g = c.group || 'Other';
                          if (!acc[g]) acc[g] = [];
                          acc[g].push(c);
                          return acc;
                        }, {});
                        const groupOrder = ['Metrics', 'Vitals', 'Body', 'Blood', 'Identity', 'Contact', 'Visit', 'Other'];
                        const orderedGroupNames = groupOrder.filter(g => groups[g]).concat(
                          Object.keys(groups).filter(g => !groupOrder.includes(g))
                        );
                        const isAuto = !customCols;
                        const toggle = (key) => {
                          // Starting from the currently effective columns so the
                          // first edit transitions cleanly from auto → custom.
                          const startingKeys = effectiveCols.map(c => c.key);
                          const has = startingKeys.includes(key);
                          const next = has
                            ? startingKeys.filter(k => k !== key)
                            : [...startingKeys, key];
                          setSpreadsheetCols(next.length > 0 ? next : null);
                        };
                        return (
                          <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', padding: 12, minWidth: 280, maxHeight: 420, overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMedium, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Columns {isAuto && <Tag color="blue" style={{ marginLeft: 4, fontSize: 10 }}>Auto</Tag>}
                              </span>
                              <Button
                                type="link"
                                size="small"
                                style={{ padding: 0, height: 'auto', fontSize: 11 }}
                                onClick={() => setSpreadsheetCols(null)}
                                disabled={isAuto}
                              >
                                Reset to auto
                              </Button>
                            </div>
                            {orderedGroupNames.map(gname => (
                              <div key={gname} style={{ marginBottom: 10 }}>
                                <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textMedium, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                  {gname}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  {groups[gname].map(c => (
                                    <Checkbox
                                      key={c.key}
                                      checked={effectiveColKeys.has(c.key)}
                                      onChange={() => toggle(c.key)}
                                      style={{ fontSize: 12 }}
                                    >
                                      {c.label}
                                    </Checkbox>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    >
                      <Button icon={<FontAwesomeIcon icon={faFilter} style={{ fontSize: 11 }} />}>
                        Columns ({effectiveCols.length})
                      </Button>
                    </Dropdown>
                    <Tooltip title="Click to export with names (identified). Use the ▾ menu to export an anonymized copy — coded Patient ID only, no names / DOB / email / phone — safe to share without leaking PHI.">
                      <Dropdown.Button
                        icon={<DownOutlined />}
                        onClick={() => exportMembersCSV(false)}
                        menu={{
                          items: [
                            {
                              key: 'identified',
                              icon: <DownloadOutlined />,
                              label: 'Export with names (identified)',
                              onClick: () => exportMembersCSV(false)
                            },
                            {
                              key: 'anonymized',
                              icon: <SafetyOutlined />,
                              label: 'Export anonymized (no PHI)',
                              onClick: () => exportMembersCSV(true)
                            }
                          ]
                        }}
                      >
                        <DownloadOutlined /> Export Members
                      </Dropdown.Button>
                    </Tooltip>
                  </ToolbarField>
                </MembersToolbar>
              )}

              {membersExpanded && (
                <MembersTable>
                  <MemberRow className="header" $valueCols={effectiveCols.length}>
                    <span onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>Member{sortIndicator('name')}</span>
                    <span onClick={() => toggleSort('age')} style={{ cursor: 'pointer', textAlign: 'right' }}>Age{sortIndicator('age')}</span>
                    <span style={{ textAlign: 'center' }}>Sex</span>
                    {effectiveCols.map(c => (
                      <span key={c.key} onClick={() => toggleSort(c.key)} className="num" style={{ cursor: 'pointer' }}>
                        {c.label}{sortIndicator(c.key)}
                      </span>
                    ))}
                    <span onClick={() => toggleSort('risk')} style={{ cursor: 'pointer' }}>Flags{sortIndicator('risk')}</span>
                  </MemberRow>
                  {rows.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                      No members match the current filter.
                    </div>
                  ) : rows.slice(0, memberRenderLimit).map(m => {
                    const displayName = identifyMembers
                      ? (m.name || '—')
                      : (m.firstName || m.lastName
                          ? `${(m.firstName || '').charAt(0).toUpperCase()}.${m.lastName ? ' ' + m.lastName.charAt(0).toUpperCase() + '.' : ''}`
                          : (m.name || '—'));
                    return (
                      <MemberRow key={m._id || m.patientId} $valueCols={effectiveCols.length}>
                        <span style={{ fontWeight: 500 }}>{displayName}</span>
                        <span className="num">{m.age ?? '–'}</span>
                        <span style={{ textAlign: 'center' }}>{m.sex?.charAt(0) || '–'}</span>
                        {effectiveCols.map(c => (
                          <span key={c.key} className="num">{colFormat(c, m)}</span>
                        ))}
                        <span>
                          {(m.flags || []).length === 0 ? (
                            <span style={{ color: '#52c41a', fontSize: 11 }}>✓ No flags</span>
                          ) : m.flags.map(f => (
                            <Tooltip key={f.key} title={f.label}>
                              <FlagChip $severity={f.severity}>{f.label.replace(/\s*\(.*\)$/, '')}</FlagChip>
                            </Tooltip>
                          ))}
                        </span>
                      </MemberRow>
                    );
                  })}
                  {rows.length > memberRenderLimit && (
                    <MembersTableFooter>
                      <span className="count">
                        Showing {memberRenderLimit.toLocaleString()} of {rows.length.toLocaleString()} members
                      </span>
                      <span className="actions">
                        <Button
                          size="small"
                          onClick={() => setMemberRenderLimit(l => Math.min(l + MEMBER_PAGE_SIZE, rows.length))}
                        >
                          Show {Math.min(MEMBER_PAGE_SIZE, rows.length - memberRenderLimit)} more
                        </Button>
                        <Button
                          size="small"
                          type="link"
                          onClick={() => setMemberRenderLimit(rows.length)}
                        >
                          Show all ({rows.length.toLocaleString()})
                        </Button>
                      </span>
                    </MembersTableFooter>
                  )}
                </MembersTable>
              )}
            </MembersSection>
          );
        })()}
      </ReportCard>
    );
  };

  // Count of active "cohort & demographics" filters, surfaced as a badge on the
  // collapsible section header so users know filters are applied while it's tucked away.
  const activeCohortCount = [
    cohortSex !== 'all',
    cohortRace !== 'all',
    cohortInsured !== 'all',
    cohortStress !== 'all',
    cohortCounties.length > 0,
    cohortZips.length > 0,
    cohortAgeMin != null || cohortAgeMax != null,
    memberLimit != null,
    prioritizeData,
    !excludeArchived,
    !ageBandsEqual(ageBands, DEFAULT_AGE_BANDS)
  ].filter(Boolean).length;

  return (
    <PageContainer>
      <FilterBar className="filters-animate-in">
        <FilterRow>
          <FilterLabel><FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 4 }} /> Date</FilterLabel>
          <Select value={quickRange} onChange={handleQuickRangeChange} style={{ width: 140 }} size="middle">
            <Option value="7days">Last 7 days</Option>
            <Option value="month">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="6months">Last 6 months</Option>
            <Option value="year">Last year</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="all">All time</Option>
            <Option value="custom">Custom</Option>
          </Select>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            format="MM/DD/YY"
            style={{ width: 230 }}
            size="middle"
            allowClear={false}
            placeholder={quickRange === 'all' ? ['All time', 'All time'] : undefined}
          />
          <FilterDivider />
          <FilterLabel><FontAwesomeIcon icon={faFilter} style={{ marginRight: 4 }} /> Scope</FilterLabel>
          <Select
            mode="multiple"
            value={eventTypeIds}
            onChange={(vals) => { setEventTypeIds(vals); setBookingFilter(null); }}
            style={{ minWidth: 220, maxWidth: 380 }}
            size="middle"
            placeholder="All Event Types"
            allowClear
            maxTagCount="responsive"
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          >
            {eventTypes.map(et => (
              <Option key={et._id} value={et._id}>{et.name}</Option>
            ))}
          </Select>
          <Select
            value={bookingFilter}
            onChange={setBookingFilter}
            style={{ width: 200 }}
            size="middle"
            placeholder={eventTypeIds.length > 1 ? 'Select one event type to scope bookings' : 'All Bookings'}
            disabled={eventTypeIds.length > 1}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
          >
            {bookings.map(b => (
              <Option key={b._id} value={b._id}>
                {b.name}{b.date ? ` (${dayjs(b.date).format('M/D/YY')})` : ''}
              </Option>
            ))}
          </Select>
          <Tooltip title="Limit to events held in a metro area or city, across every calendar. Uses the location saved on each event, so a National Tour stop in Dallas counts under Dallas. Events with no city recorded cannot match.">
            <Select
              value={eventLocation}
              onChange={(v) => setEventLocation(v || null)}
              style={{ minWidth: 200, maxWidth: 300 }}
              size="middle"
              placeholder="Any location"
              allowClear
              showSearch
              optionFilterProp="label"
              options={[
                ...(locationOptions.metros.length > 0
                  ? [{ label: 'Metro areas', options: locationOptions.metros.map(m => ({ value: m.value, label: m.label })) }]
                  : []),
                ...(locationOptions.cities.length > 0
                  ? [{ label: 'Cities', options: locationOptions.cities.map(c => ({ value: c.value, label: c.label })) }]
                  : [])
              ]}
            />
          </Tooltip>
        </FilterRow>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap'
        }}>
          <Tooltip title="Sex, race, insurance, stress, ZIP, limit, age groups and other filters that reshape the whole report.">
            <Button
              size="middle"
              onClick={() => setCohortPanelOpen(o => !o)}
              icon={<FontAwesomeIcon icon={faUsers} style={{ fontSize: 12 }} />}
            >
              Cohort &amp; Demographics
              {activeCohortCount > 0 && (
                <Tag color="blue" style={{ marginLeft: 6, marginRight: 0 }}>{activeCohortCount}</Tag>
              )}
              <FontAwesomeIcon icon={cohortPanelOpen ? faChevronDown : faChevronRight} style={{ marginLeft: 6, fontSize: 11 }} />
            </Button>
          </Tooltip>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {selectedMetrics.length > 0 && (
              <Button
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => { setSelectedMetrics([]); setReportData(null); setLastGenSnapshot(null); }}
              >
                Clear
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={generateReport}
              loading={loading}
              disabled={selectedMetrics.length === 0}
              size="middle"
            >
              Generate Report
            </Button>
          </div>
        </div>
        {cohortPanelOpen && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <FilterLabel>
            <Tooltip title="Cohort filters reshape the entire report. Aggregates, charts, and the AI narrative all reflect only the selected cohort. Click Generate Report to apply changes.">
              <FontAwesomeIcon icon={faUsers} style={{ marginRight: 4 }} /> Cohort <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 11, color: COLORS.textMedium }} />
            </Tooltip>
          </FilterLabel>
          <Select
            value={cohortSex}
            onChange={setCohortSex}
            style={{ width: 140 }}
            size="middle"
            showSearch
            optionFilterProp="label"
            options={[
              { value: 'all', label: 'Any sex' },
              { value: 'Male', label: 'Male' },
              { value: 'Female', label: 'Female' },
              { value: 'Other', label: 'Other' },
              { value: 'Unknown', label: 'Unknown' }
            ]}
          />
          <Select
            value={cohortRace}
            onChange={setCohortRace}
            style={{ width: 280 }}
            size="middle"
            showSearch
            optionFilterProp="label"
            options={[
              { value: 'all', label: 'Any race / ethnicity' },
              ...RACE_OPTIONS
            ]}
          />
          <Tooltip title="Filter members by insurance status from their intake (bus) form. Insured = any coverage (Private, Medicare, Medicaid, etc.); Uninsured = none.">
            <Select
              value={cohortInsured}
              onChange={setCohortInsured}
              style={{ width: 150 }}
              size="middle"
              options={[
                { value: 'all', label: 'Any insurance' },
                { value: 'insured', label: 'Insured' },
                { value: 'uninsured', label: 'Uninsured' }
              ]}
            />
          </Tooltip>
          <Tooltip title="Filter members by their intake-form stress rating (1-5). Low = 1-2, Moderate = 3, High = 4-5.">
            <Select
              value={cohortStress}
              onChange={setCohortStress}
              style={{ width: 150 }}
              size="middle"
              options={[
                { value: 'all', label: 'Any stress' },
                { value: 'low', label: 'Low stress' },
                { value: 'moderate', label: 'Moderate stress' },
                { value: 'high', label: 'High stress' }
              ]}
            />
          </Tooltip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <FilterLabel>
            <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 4 }} /> Area
          </FilterLabel>
          <Tooltip title="Filter members by residential ZIP. Type each ZIP and press Enter (or separate with commas/spaces). Use a full 5-digit ZIP for an exact match, or 3 digits for a regional (ZIP3) prefix. Members matching ANY listed ZIP are included.">
            <Select
              mode="tags"
              value={cohortZips}
              onChange={(vals) => {
                const cleaned = Array.from(new Set(
                  (vals || [])
                    .map(v => (v || '').toString().replace(/\D/g, '').slice(0, 5))
                    .filter(z => z.length === 5 || z.length === 3)
                ));
                setCohortZips(cleaned);
              }}
              tokenSeparators={[',', ' ']}
              open={false}
              suffixIcon={null}
              placeholder="ZIP(s) — 5-digit or 3-digit prefix"
              style={{ minWidth: 240, maxWidth: 360 }}
              size="middle"
              allowClear
            />
          </Tooltip>
          <Tooltip title="Filter members by one or more counties (derived from their ZIP via the HUD ZIP-County crosswalk). Members in ANY selected county are included. Only counties present in your data are listed.">
            <Select
              mode="multiple"
              value={cohortCounties}
              onChange={setCohortCounties}
              style={{ minWidth: 220, maxWidth: 380 }}
              size="middle"
              showSearch
              allowClear
              maxTagCount="responsive"
              optionFilterProp="label"
              placeholder="Any county"
              options={counties.map(c => ({ value: c.fips, label: c.label }))}
            />
          </Tooltip>
          <Tooltip title="Restrict the cohort to an age band (inclusive years). Combine with sex, race, and location for cuts like 'Black women ages 31–45 in Houston'. Members with no usable date of birth are excluded when an age filter is set.">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: COLORS.textDark, whiteSpace: 'nowrap' }}>Age</span>
              <InputNumber
                value={cohortAgeMin}
                onChange={(v) => setCohortAgeMin(Number.isFinite(v) ? v : null)}
                min={0}
                max={120}
                placeholder="Min"
                style={{ width: 74 }}
                size="middle"
                controls={false}
              />
              <span style={{ color: COLORS.textMedium }}>–</span>
              <InputNumber
                value={cohortAgeMax}
                onChange={(v) => setCohortAgeMax(Number.isFinite(v) ? v : null)}
                min={0}
                max={120}
                placeholder="Max"
                style={{ width: 74 }}
                size="middle"
                controls={false}
              />
            </div>
          </Tooltip>
        </div>
        <FilterRow>
          <FilterLabel><FontAwesomeIcon icon={faUsers} style={{ marginRight: 4 }} /> Limit</FilterLabel>
          <InputNumber
            value={memberLimit}
            onChange={setMemberLimit}
            min={1}
            max={10000}
            placeholder="Latest # members"
            style={{ width: 160 }}
            size="middle"
            controls={false}
          />
          <FilterDivider />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={excludeArchived} onChange={setExcludeArchived} />
            <span style={{ fontSize: 13, color: COLORS.textDark, whiteSpace: 'nowrap' }}>Exclude Archived</span>
          </div>
          <FilterDivider />
          <Tooltip title="When ON, the cohort fills the limit with members who actually have data for one of your selected metrics first, then fills remaining slots with the most recently active members. Useful when a metric (like Visceral Fat) is sparse and the latest N members happen not to have that test.">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Switch
                size="small"
                checked={prioritizeData}
                onChange={setPrioritizeData}
                disabled={!memberLimit || selectedMetrics.length === 0}
              />
              <span style={{ fontSize: 13, color: COLORS.textDark, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                Prioritize members with selected metric data
                <InfoCircleOutlined style={{ fontSize: 11, color: COLORS.textMedium }} />
              </span>
            </div>
          </Tooltip>
          <FilterDivider />
          <Dropdown
            open={ageBandsOpen}
            onOpenChange={setAgeBandsOpen}
            trigger={['click']}
            placement="bottomLeft"
            dropdownRender={() => {
              // Normalize bands, keeping labels in sync with their numeric range
              // unless the user typed a custom label. `max: null` = open-ended.
              const setBand = (idx, patch) => {
                setAgeBands(prev => prev.map((b, i) => {
                  if (i !== idx) return b;
                  const next = { ...b, ...patch };
                  // Auto-derive label from range unless a custom label was set.
                  if (!b._customLabel || 'min' in patch || 'max' in patch) {
                    next.label = bandLabel(next.min, next.max);
                  }
                  return next;
                }));
              };
              const removeBand = (idx) => setAgeBands(prev => prev.filter((_, i) => i !== idx));
              const addBand = () => setAgeBands(prev => {
                const last = prev[prev.length - 1];
                const nextMin = last && last.max != null ? last.max + 1 : (last ? (last.min + 10) : 0);
                return [...prev, { label: bandLabel(nextMin, nextMin + 9), min: nextMin, max: nextMin + 9 }];
              });
              return (
                <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', padding: 14, width: 340 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMedium, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                    Age Group Presets
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                    {AGE_BAND_PRESETS.map(p => (
                      <Button
                        key={p.key}
                        size="small"
                        type={ageBandsEqual(ageBands, p.bands) ? 'primary' : 'default'}
                        style={{ textAlign: 'left', justifyContent: 'flex-start', fontSize: 12 }}
                        onClick={() => setAgeBands(p.bands.map(b => ({ ...b })))}
                        block
                      >
                        {p.label}
                      </Button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMedium, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Custom Groups</span>
                    <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 10, color: COLORS.textMedium }}>
                      blank max = &amp; older
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                    {ageBands.map((b, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <InputNumber
                          size="small"
                          min={0}
                          max={150}
                          value={b.min}
                          onChange={(v) => setBand(idx, { min: v ?? 0 })}
                          style={{ width: 64 }}
                          placeholder="min"
                        />
                        <span style={{ color: COLORS.textMedium }}>–</span>
                        <InputNumber
                          size="small"
                          min={0}
                          max={150}
                          value={b.max}
                          onChange={(v) => setBand(idx, { max: (v === null || v === undefined) ? null : v })}
                          style={{ width: 64 }}
                          placeholder="+"
                        />
                        <span style={{ flex: 1, fontSize: 12, color: COLORS.textDark, marginLeft: 4 }}>{b.label}</span>
                        <Button
                          size="small"
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => removeBand(idx)}
                          disabled={ageBands.length <= 1}
                          style={{ color: ageBands.length <= 1 ? '#ccc' : '#cf1322' }}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
                    <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }} onClick={addBand}>
                      + Add group
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      style={{ padding: 0, fontSize: 12 }}
                      onClick={() => setAgeBands(DEFAULT_AGE_BANDS.map(b => ({ ...b })))}
                      disabled={ageBandsEqual(ageBands, DEFAULT_AGE_BANDS)}
                    >
                      Reset to default
                    </Button>
                  </div>
                </div>
              );
            }}
          >
            <Tooltip title="Customize the age groups used in every 'by age' breakdown and the demographics age chart. Re-Generate to apply.">
              <Button size="middle" icon={<FontAwesomeIcon icon={faUsers} style={{ fontSize: 12 }} />}>
                Age Groups ({ageBands.length}){!ageBandsEqual(ageBands, DEFAULT_AGE_BANDS) && ' •'}
              </Button>
            </Tooltip>
          </Dropdown>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Tooltip title="Download one row per patient with raw age (no bucketing). Honors the date, scope, and cohort filters above. Useful for funder-specific age groupings.">
              <Button
                size="middle"
                icon={<DownloadOutlined />}
                onClick={() => setAgeExportOpen(true)}
              >
                Export Raw Ages
              </Button>
            </Tooltip>
          </div>
        </FilterRow>
        <div style={{ fontSize: 12, color: COLORS.textMedium, fontStyle: 'italic', marginTop: 6 }}>
          Cohort &amp; demographics filters reshape the report — re-Generate to apply.
        </div>
        </div>
        )}
      </FilterBar>

      <div style={{
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 4px',
        flexWrap: 'wrap',
        gap: 12
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.textDark }}>
          Select Metrics
          {selectedMetrics.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>{selectedMetrics.length} selected</Tag>
          )}
        </div>
        <Input
          placeholder="Search metrics..."
          prefix={<SearchOutlined style={{ color: COLORS.textMuted }} />}
          value={metricSearch}
          onChange={e => { setMetricSearch(e.target.value); setActiveTemplate(null); }}
          allowClear
          style={{ width: 220 }}
        />
      </div>

      <div style={{ marginBottom: 16, padding: '0 4px' }}>
        <TemplateBar>
          <FontAwesomeIcon icon={faBolt} style={{ color: COLORS.textMuted, fontSize: 14 }} />
          <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 500 }}>Quick Templates:</span>
          {REPORT_TEMPLATES.map(tpl => (
            <TemplateChip
              key={tpl.key}
              $active={activeTemplate === tpl.key}
              onClick={() => applyTemplate(tpl)}
            >
              {tpl.label}
            </TemplateChip>
          ))}
        </TemplateBar>
      </div>

      <div style={{ marginBottom: 28 }}>
        {SECTION_GROUPS.map((group, groupIdx) => {
          const searchLower = metricSearch.toLowerCase();
          const groupCategories = METRIC_CATEGORIES
            .filter(cat => cat.group === group.label)
            .filter(cat =>
              !metricSearch ||
              cat.category.toLowerCase().includes(searchLower) ||
              cat.metrics.some(m => m.label.toLowerCase().includes(searchLower) || m.description?.toLowerCase().includes(searchLower))
            );
          if (groupCategories.length === 0) return null;
          const isCollapsed = collapsedSections[group.key];
          return (
            <div key={group.key}>
              <SectionGroupHeader $first={groupIdx === 0} onClick={() => toggleSection(group.key)}>
                {isCollapsed ? <FontAwesomeIcon icon={faChevronRight} /> : <FontAwesomeIcon icon={faChevronDown} />}
                {group.icon}
                <SectionGroupLabel>{group.label}</SectionGroupLabel>
                <SectionGroupCount>{groupCategories.length} {groupCategories.length === 1 ? 'category' : 'categories'}</SectionGroupCount>
              </SectionGroupHeader>
              <CollapsibleContent $collapsed={isCollapsed}>
                <div>
                <Row gutter={[16, 16]} style={{ marginBottom: isCollapsed ? 0 : 16 }}>
                  {groupCategories.map(cat => {
                    const filteredMetrics = metricSearch
                      ? cat.metrics.filter(m =>
                          m.label.toLowerCase().includes(searchLower) ||
                          m.description?.toLowerCase().includes(searchLower) ||
                          cat.category.toLowerCase().includes(searchLower)
                        )
                      : cat.metrics;
                    return (
                      <Col xs={24} sm={24} md={12} key={cat.category}>
                        <MetricSelectorCard>
                          <CategoryHeader>
                            {cat.icon}
                            <span>{cat.category}</span>
                            {cat.modalContent && (
                              <InfoCircleOutlined
                                style={{ fontSize: 13, color: COLORS.textMuted, cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); setInfoModal(cat.modalContent); }}
                              />
                            )}
                            <span
                              style={{ fontSize: 11, color: COLORS.$primaryBlue, cursor: 'pointer', marginLeft: 'auto', fontWeight: 400 }}
                              onClick={() => selectAllInCategory(cat.category)}
                            >
                              {cat.metrics.every(m => selectedMetrics.includes(m.key)) ? 'Deselect All' : 'Select All'}
                            </span>
                          </CategoryHeader>
                          <MetricCheckboxGroup>
                            {filteredMetrics.map(metric => (
                              <Tooltip title={metric.description} key={metric.key} placement="top">
                                <MetricCheckbox
                                  $selected={selectedMetrics.includes(metric.key)}
                                  onClick={() => toggleMetric(metric.key)}
                                >
                                  <Checkbox
                                    checked={selectedMetrics.includes(metric.key)}
                                    onChange={() => toggleMetric(metric.key)}
                                    style={{ pointerEvents: 'none' }}
                                  />
                                  {metric.label}
                                </MetricCheckbox>
                              </Tooltip>
                            ))}
                          </MetricCheckboxGroup>
                        </MetricSelectorCard>
                      </Col>
                    );
                  })}
                </Row>
                </div>
              </CollapsibleContent>
            </div>
          );
        })}
      </div>

      <div ref={resultsRef} style={{ scrollMarginTop: 16 }}>
        {loading ? (
          <ReportCard>
            <Skeleton active paragraph={{ rows: 2 }} style={{ marginBottom: 16 }} />
            <Row gutter={[20, 20]}>
              {[0, 1, 2, 3].map(i => (
                <Col xs={12} sm={8} md={6} key={i}>
                  <ResultStatCard>
                    <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: '40%' }} />
                  </ResultStatCard>
                </Col>
              ))}
            </Row>
            <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 20 }} />
          </ReportCard>
        ) : reportData ? renderResults() : (
          selectedMetrics.length > 0 && (
            <Empty
              description="Click 'Generate Report' to see results"
              style={{ padding: 60, background: '#fafafa', borderRadius: 12 }}
            />
          )
        )}
      </div>

      {filtersDirty && !loading && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: '#fff',
            border: '1px solid #ffd591',
            borderRadius: 24,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: '8px 10px 8px 16px'
          }}
        >
          <InfoCircleOutlined style={{ color: '#fa8c16' }} />
          <span style={{ fontSize: 13, color: COLORS.textDark }}>Filters changed</span>
          <Button
            type="primary"
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={generateReport}
            disabled={selectedMetrics.length === 0}
            style={{ borderRadius: 16 }}
          >
            Re-generate
          </Button>
        </div>
      )}

      {reportData && (
        <NarrativeCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FontAwesomeIcon icon={faBolt} style={{ fontSize: 16, color: COLORS.$primaryBlue }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.textDark }}>Smart Summary</span>
            </div>
            <NarrativeControls>
              <Select
                value={narrativeStyle}
                onChange={setNarrativeStyle}
                size="middle"
                options={[
                  { value: 'grant', label: 'Grant Narrative' },
                  { value: 'executive', label: 'Executive Summary' },
                  { value: 'community', label: 'Community Impact Story' },
                  { value: 'trends', label: 'Trend Analysis' }
                ]}
              />
              <Button
                type="primary"
                size="middle"
                icon={<ThunderboltOutlined />}
                onClick={generateNarrative}
                loading={narrativeLoading}
              >
                Generate
              </Button>
            </NarrativeControls>
          </div>
          <NarrativeStyleHints>
            {narrativeStyle === 'grant' && 'Formal, evidence-based language for grant applications and funders.'}
            {narrativeStyle === 'executive' && 'Concise bullet-pointed summary for leadership and board members.'}
            {narrativeStyle === 'community' && 'Warm, accessible language for newsletters and community outreach.'}
            {narrativeStyle === 'trends' && 'Data-driven analysis of screening trends, patterns, and changes over time.'}
          </NarrativeStyleHints>
          {narrativeText ? (
            <div>
              <NarrativeOutput>
                {renderNarrativeFormatted(narrativeText)}
                {narrativeLoading && <NarrativeCursor />}
              </NarrativeOutput>
              {!narrativeLoading && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <Button size="small" onClick={copyNarrative}>Copy to Clipboard</Button>
                </div>
              )}
            </div>
          ) : (
            !narrativeLoading && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: COLORS.textMuted, fontSize: 13 }}>
                Select a style and click Generate to create a smart summary from your report data.
              </div>
            )
          )}
        </NarrativeCard>
      )}

      <Modal
        open={!!infoModal}
        onCancel={() => setInfoModal(null)}
        footer={null}
        width={560}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <InfoCircleOutlined style={{ color: COLORS.$primaryBlue, fontSize: 18 }} />
            <span>{infoModal?.title}</span>
          </div>
        }
      >
        {infoModal && (
          <div style={{ padding: '4px 0' }}>
            <p style={{ fontSize: 14, color: COLORS.textMedium, lineHeight: 1.6, marginBottom: 16 }}>
              {infoModal.description}
            </p>

            {infoModal.guidelines && (
              <div style={{
                background: '#f0f7ff',
                border: '1px solid #d6e8fa',
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 16,
                fontSize: 13,
                color: COLORS.textDark,
                whiteSpace: 'pre-line'
              }}>
                <strong style={{ display: 'block', marginBottom: 4, color: COLORS.$primaryBlue }}>Clinical Guidelines</strong>
                {infoModal.guidelines}
              </div>
            )}

            {infoModal.note && (
              <div style={{
                background: '#fffbe6',
                border: '1px solid #ffe58f',
                borderRadius: 8,
                padding: '10px 14px',
                marginBottom: 16,
                fontSize: 13,
                color: '#8c6d1f'
              }}>
                <strong>Note: </strong>{infoModal.note}
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: 13, color: COLORS.textDark, display: 'block', marginBottom: 10 }}>
                Available Metrics
              </strong>
              {infoModal.metrics.map((m, i) => (
                <div key={i} style={{
                  marginBottom: 10,
                  paddingLeft: 12,
                  borderLeft: `3px solid ${COLORS.$primaryBlue}`,
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.textDark, marginBottom: 2 }}>
                    {m.name}
                  </div>
                  <div style={{ fontSize: 13, color: COLORS.textMedium, lineHeight: 1.5 }}>
                    {m.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Export Raw Ages (CSV)"
        open={ageExportOpen}
        onCancel={() => setAgeExportOpen(false)}
        onOk={handleExportRawAges}
        confirmLoading={ageExportLoading}
        okText="Download CSV"
        width={560}
      >
        <div style={{ fontSize: 13, color: COLORS.textMedium, marginBottom: 16 }}>
          One row per patient with their raw age — no preset bucketing. Honors the
          current Date, Scope (event type/booking, exclude archived), and Cohort
          (sex / race / zip) filters above. Use this when funders need their own
          age groupings.
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textDark, marginBottom: 6 }}>
            Optional funder bins
          </div>
          <Input
            value={ageExportBins}
            onChange={(e) => setAgeExportBins(e.target.value)}
            placeholder="e.g. 18-34,35-49,50-64,65+"
            allowClear
          />
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
            Comma-separated ranges. Use <code>N+</code> for an open-ended top bin.
            Adds a derived <code>bucket</code> column; raw age is always included.
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textDark, marginBottom: 6 }}>
            ZIP format
          </div>
          <Select
            value={ageExportZipMode}
            onChange={setAgeExportZipMode}
            style={{ width: 220 }}
            options={[
              { value: 'full', label: 'Full 5-digit ZIP' },
              { value: 'zip3', label: 'ZIP3 prefix only (de-identified)' }
            ]}
          />
        </div>
      </Modal>
    </PageContainer>
  );
};

export default Reports;
